import { createEffect, onCleanup, createSignal, Show, untrack, type Accessor, type Setter, For } from "solid-js";
import { sanitizeTooltipHtml } from '@utils/htmlSanitization';
import * as d3 from "d3";
import { myTickFormat, formatDateTime } from "../../utils/global";
import { useChartCleanup } from "../../utils/d3Cleanup";
import { warn, error as logError, log, debug } from "../../utils/console";
import { lasso } from "../../utils/d3-lasso";
import LassoSelectionWorker from "../../workers/lasso-selection-processor.ts?worker";

import { setTooltip, tooltip } from "../../store/globalStore";
import { isDark } from "../../store/themeStore";
import { cutEvents, selectedRange, selectedRanges, hasSelection, isCut, selectedEvents } from "../../store/selectionStore";
import { selectedStatesTimeseries, selectedRacesTimeseries, selectedLegsTimeseries, selectedGradesTimeseries, startDate, endDate } from "../../store/filterStore";
import { setSelectedTime, requestTimeControl, releaseTimeControl } from "../../store/playbackStore";
import { persistantStore } from "../../store/persistantStore";
import { applyDataFilter, filterByTwa } from "../../utils/dataFiltering";
import { enhancedScatterWorkerManager } from "../../utils/enhancedScatterWorkerManager";
import { computeEnhancedScatterFit } from "./enhancedScatterFitTask";
import { createDensityOptimizationCacheKey } from "../../utils/densityOptimizationCache";
import { setCurrentDataset, getCurrentDatasetTimezone } from "../../store/datasetTimezoneStore";
import { defaultChannelsStore } from "../../store/defaultChannelsStore";
import Table from "./FitTable";
import { clipLineSegmentToBounds, clipPolylineToBounds } from "../../utils/scatterTrendClip";
import { isScatterPointHighlighted, type ScatterLegendHighlightState } from "../../utils/scatterHistoryColorLegend";
import { applyScatterHistoryClientFilters } from "../../utils/scatterHistoryChartFilters";
import type { ScatterHistoryDataMode } from "../../services/scatterHistoryDataService";
import { buildExploreCategoryColorMap } from "../../utils/exploreHistoryColorGroups";
import {
  buildColorChannelLegendModel,
  buildColorChannelScale,
  buildDayColorMapForScatter,
  buildSourceColorMapForScatter,
  getFitTableLegendColor,
  getScatterPointColor,
  type ColorChannelLegendModel,
} from "../../utils/scatterColors";
import ScatterColorChannelLegend from "./ScatterColorChannelLegend";
import { computeScatterTooltipPosition } from "../../utils/scatterTooltipPosition";
import {
  mountSimpleScatterUPlot,
  type SimpleScatterUPlotMount,
  type SimpleScatterUPlotPoint,
} from "../../utils/simpleScatterUPlot";
import "uplot/dist/uPlot.min.css";
import { shouldUseSimpleScatterUPlot } from "../../utils/simpleScatterUPlotGate";
import { buildUPlotOverlayLassoPoints, uPlotValToOverlayCoords } from "../../utils/uPlotScatterLassoCoords";
import { setScatterSelectionBusyCursor } from "../../utils/scatterSelectionBusyCursor";

/** SVG height for a single row of three charts (`col3`) — double the old 400px floor. */
const COL3_ROW_HEIGHT_PX = 800;
/** SVG height for a single row of two charts (`col2`) — 50% more than the old flat 600. */
const COL2_ROW_HEIGHT_PX = 900;
/**
 * Share of the viewport one chart takes in the two-rows-of-two grid (`col2x2`), so the two rows
 * together fill ~90% of the page. Unlike the single-row layouts this is a proportion, not a target:
 * both rows have to fit at once, so the height has to follow the page rather than aim at a number.
 */
const COL2X2_CELL_VIEWPORT_SHARE = 0.45;
/**
 * Room a single-row grid leaves for everything that is not the plot: the 60px app header plus the
 * grid's own top pad and the bottom slack the x-axis channel label needs. Without it a tall row
 * pushed the label past the bottom of the scroll viewport and it was drawn clipped.
 */
const ROW_VIEWPORT_RESERVE_PX = 160;

/**
 * Distance from the SVG's bottom edge to the x-axis channel label's anchor. The label hangs DOWN
 * from its anchor (`dominant-baseline: hanging` in Styles.css), so this has to exceed the label's
 * line height or the glyphs are clipped by the box edge. The 50px bottom margin leaves room for the
 * tick labels above it.
 */
const X_LABEL_HANGING_CLEARANCE_PX = 24;

/** A single grid row: the layout's target height, but never taller than the page can show. */
function singleRowHeightPx(targetPx: number, viewportPx: number): number {
  return Math.max(400, Math.min(targetPx, viewportPx - ROW_VIEWPORT_RESERVE_PX));
}

/** One cell of the two-rows-of-two grid: a share of the page, so both rows fit together. */
function col2x2CellHeightPx(viewportPx: number): number {
  return Math.max(320, Math.round(viewportPx * COL2X2_CELL_VIEWPORT_SHARE));
}

/**
 * Share of the viewport one row takes in a multi-row three-column grid (`col3x`). Like `col2x2`
 * this is a proportion: two rows land just above 40% each and fill the page; three or more rows
 * scroll inside `.performance-charts-scroll-inner`.
 */
const COL3X_ROW_VIEWPORT_SHARE = 0.42;

/** One cell of a multi-row three-column grid: a share of the page per row. */
function col3xCellHeightPx(viewportPx: number): number {
  return Math.max(360, Math.round(viewportPx * COL3X_ROW_VIEWPORT_SHARE));
}

type RegressionMethod = "None" | "Linear" | "Poly 2" | "Poly 3" | "Loess 0.3" | "Loess 0.5";

interface FitGroup {
  groupName: string;
  color: string;
  regression?: { slope?: number; points: { x: number; y: number }[]; r2?: number };
  tableValues?: { x: number; y: number }[];
  density?: number;
}

function buildTableFitsFromGroups(
  groups: FitGroup[],
  chart: { colorType?: string; colorChannel?: { name?: string }; color?: string },
  colortype: string
) {
  return groups
    .filter((group) => group.tableValues && group.tableValues.length > 0)
    .map((group) => ({
      group: group.groupName,
      // Match the drawn trend line + scatter points: for DEFAULT + fixed series color this uses
      // series.color; otherwise the worker group color (which now carries the correct group palette).
      color: getFitTableLegendColor(chart, colortype, group.color),
      fitValues: group.tableValues!.map((tv) => ({ x: tv.x, y: tv.y })),
      pointCount: group.density ?? 0,
    }));
}

type CutEventRange = { start_time?: string; end_time?: string; event_id?: number } | number;

function isCutViewActive(
  cutIds: Set<number>,
  isCut: boolean,
  cutEventRanges: CutEventRange[]
): boolean {
  return cutIds.size > 0 || (isCut && cutEventRanges.length > 0);
}

function filterScatterPointsByIds<T extends { ID?: number }>(data: T[], ids: Set<number>): T[] {
  if (ids.size === 0) {return data;}
  return data.filter(
    (d) => d.ID !== undefined && d.ID !== null && ids.has(Number(d.ID))
  );
}

function isPointVisibleInCut(
  d: { ID?: number; Datetime?: string | Date },
  cutIds: Set<number>,
  cutEventRanges: CutEventRange[]
): boolean {
  if (cutIds.size > 0) {
    return d.ID !== undefined && d.ID !== null && cutIds.has(Number(d.ID));
  }
  if (cutEventRanges.length === 0) {
    return true;
  }
  return filterPointsByCut([d], true, cutIds, cutEventRanges).length > 0;
}

function resolveScatterPointX(d: Record<string, unknown>, xAxisChannelName: string): number {
  const direct = Number(d.x ?? d.X);
  if (Number.isFinite(direct)) {return direct;}
  const raw =
    d[xAxisChannelName] ??
    d[xAxisChannelName.toLowerCase()] ??
    d[xAxisChannelName.toUpperCase()];
  const val = Number(raw);
  return Number.isFinite(val) ? val : NaN;
}

function resolveScatterPointY(d: Record<string, unknown>, yAxisChannelName: string): number {
  const direct = Number(d.y);
  if (Number.isFinite(direct)) {return direct;}
  const raw =
    d[yAxisChannelName] ??
    d[yAxisChannelName.toLowerCase()] ??
    d[yAxisChannelName.toUpperCase()];
  const val = Number(raw);
  return Number.isFinite(val) ? val : NaN;
}

function buildScatterScales(
  xSource: Array<{ x?: number }>,
  ySource: Array<Record<string, unknown>>,
  yAxisChannelName: string,
  xywidth: number,
  xyheight: number
): { xScale: d3.ScaleLinear<number, number>; yScale: d3.ScaleLinear<number, number> } {
  const yValues = ySource
    .map((d) => resolveScatterPointY(d, yAxisChannelName))
    .filter((val) => Number.isFinite(val));
  const yMin = yValues.length > 0 ? (d3.min(yValues) as number) : 0;
  const yMax = yValues.length > 0 ? (d3.max(yValues) as number) : 1;
  const ySpan = yMax - yMin || 1;
  const yDomainMin = yMin - ySpan * 0.01;
  let yDomainMax = yMax + ySpan * 0.10;
  const maxPointRadiusPx = 18;
  const domainSpanBeforeRadius = yDomainMax - yDomainMin;
  yDomainMax += maxPointRadiusPx * (domainSpanBeforeRadius / xyheight);

  const xValues = xSource
    .map((d) => Number(d.x))
    .filter((val) => Number.isFinite(val));
  const xMin = xValues.length > 0 ? (d3.min(xValues) as number) : 0;
  const xMax = xValues.length > 0 ? (d3.max(xValues) as number) : 1;
  const xSpan = xMax - xMin || 1;
  const xBuffer = xSpan * 0.01;

  const yScale = d3.scaleLinear().domain([yDomainMin, yDomainMax]).range([xyheight, 0]);
  const xScale = d3
    .scaleLinear()
    .domain([xMin - xBuffer, xMax + xBuffer])
    .range([0, xywidth]);
  return { xScale, yScale };
}

function getScaleSourceForCut(
  preCutData: Array<{ x?: number; y?: number; ID?: number; Datetime?: string | Date }>,
  cutViewActive: boolean,
  cutIds: Set<number>,
  cutEventRanges: CutEventRange[]
) {
  if (!cutViewActive) {return preCutData;}
  if (cutIds.size > 0) {return filterScatterPointsByIds(preCutData, cutIds);}
  return filterPointsByCut(preCutData, true, cutIds, cutEventRanges);
}

function filterPointsByCut<T extends { ID?: number; Datetime?: string | Date }>(
  data: T[],
  cutViewActive: boolean,
  cutIds: Set<number> | undefined,
  cutEventRanges: CutEventRange[]
): T[] {
  if (!cutViewActive) {return data;}
  if (cutIds && cutIds.size > 0) {
    return data.filter(
      (d) => d.ID !== undefined && d.ID !== null && cutIds.has(Number(d.ID))
    );
  }
  if (cutEventRanges.length > 0) {
    return data.filter((d) => {
      if (!d.Datetime) {return false;}
      const datetime = new Date(d.Datetime);
      return cutEventRanges.some((range) => {
        if (typeof range === "number") {return false;}
        if (range.start_time && range.end_time) {
          return (
            datetime >= new Date(range.start_time) &&
            datetime <= new Date(range.end_time)
          );
        }
        return false;
      });
    });
  }
  return data;
}

function buildScatterGlobalFiltersForCache(dataMode?: ScatterHistoryDataMode) {
  const globals = {
    states: selectedStatesTimeseries(),
    races: selectedRacesTimeseries(),
    legs: selectedLegsTimeseries(),
    grades: selectedGradesTimeseries(),
  };
  if (!dataMode) {
    return globals;
  }
  return {
    ...globals,
    dateStart: startDate(),
    dateEnd: endDate(),
  };
}

// Fit computation runs on a shared @nodets pool (see ./enhancedScatterFitTask.ts) instead of blocking
// the main thread; the pool falls back to inline when workers are unavailable, so behaviour is unchanged.
async function computeFitGroups(
  sourceData: Array<{ x?: number; y?: number; ID?: number }>,
  selectedIds: Set<number>,
  chart: { colorType?: string; colorChannel?: { name?: string }; color?: string },
  regressionMethod: RegressionMethod,
  colortype: string,
  tableRange: { min: number; max: number; step: number }
): Promise<FitGroup[]> {
  if (regressionMethod === "None" || !sourceData?.length) {return [];}

  let fitInput = sourceData;
  if (selectedIds.size > 0) {
    fitInput = sourceData.filter(
      (d) => d.ID !== undefined && d.ID !== null && selectedIds.has(Number(d.ID))
    );
    if (fitInput.length < 2) {return [];}
  }

  const skipOptimization = selectedIds.size > 0 || fitInput.length <= 5000;

  // Build the same group color maps the point-rendering path uses (processDataWithEnhancedWorker), so the
  // fit trend lines and table columns are colored by the color grouping instead of falling back to the
  // default blue. Keys come from the same filtered rows the points use, so colors line up.
  const ct = (colortype || "DEFAULT") as string;
  let sourceColorMap: Record<string, string> | undefined;
  let dayColorMap: Record<string, string> | undefined;
  let categoryColorMap: Record<string, string> | undefined;
  if (ct === "SOURCE") {
    sourceColorMap = buildSourceColorMapForScatter(fitInput as Record<string, unknown>[]);
  } else if (ct === "DAY") {
    dayColorMap = buildDayColorMapForScatter(fitInput as Record<string, unknown>[]);
  } else if (ct === "EVENT" || ct === "YEAR" || ct === "CONFIG" || ct === "CREW") {
    categoryColorMap = buildExploreCategoryColorMap(fitInput as Record<string, unknown>[], ct);
  }

  const result = await computeEnhancedScatterFit(fitInput, {
    xField: "x",
    yField: "y",
    colorField:
      chart.colorType === "By Channel" && chart.colorChannel?.name
        ? chart.colorChannel.name
        : undefined,
    colorType: ct as "DEFAULT" | "TACK" | "GRADE" | "UW/DW",
    maxPoints: 3000,
    regressionMethod,
    tableRange,
    skipOptimization,
    sourceColorMap,
    dayColorMap,
    categoryColorMap,
  });
  return result.groups as FitGroup[];
}

// The fit table's x-column must span the visible x-axis domain (not a fixed range) so it matches the
// chart's x-axis after zoom. Sample the fit densely across the axis domain so the table can interpolate a
// value at each displayed tick; FitTable renders the actual axis ticks (~(max-min)/10 interval).
function buildFitTableRange(
  xScale: { domain: () => number[] } | null | undefined
): { min: number; max: number; step: number } {
  const domain = xScale?.domain?.();
  if (!domain || domain.length < 2) {return { min: 6, max: 20, step: 1 };}
  const min = Number(domain[0]);
  const max = Number(domain[1]);
  const span = max - min;
  if (!Number.isFinite(min) || !Number.isFinite(max) || span <= 0) {
    return { min: 6, max: 20, step: 1 };
  }
  // ~40 samples across the domain: fine enough for accurate interpolation at each table tick.
  return { min, max, step: span / 40 };
}

function setSimpleScatterLassoComputingCursor(on: boolean): void {
  setScatterSelectionBusyCursor(on);
}

const { selectedClassName, selectedProjectId, selectedDatasetId } = persistantStore;

interface SimpleScatterProps {
  chart?: Record<string, unknown>;
  totalCharts?: number;
  colortype?: string;
  class_name?: string;
  showFilterStatus?: boolean;
  handleZoom?: (info: unknown) => void;
  zoom?: boolean;
  onReady?: () => void;
  showFitTable?: boolean;
  useUPlot?: boolean;
  yAxisLabelNudgeRightPx?: number;
  enableLasso?: boolean;
  legendHighlight?: ScatterLegendHighlightState | null;
  /** When set, render only this legend group's points and compute the fit from only that group. */
  legendFilterGroup?: ScatterLegendHighlightState | null;
  chartLegendLabel?: string;
  /** When provided, lasso selection is shared across all scatter plots on the page */
  sharedSelectedPointIds?: Accessor<Set<number>>;
  setSharedSelectedPointIds?: Setter<Set<number>>;
  /** Called with the full selected ID set after each lasso (parent syncs selectionStore) */
  onPointSelectionChange?: (ids: Set<number>) => void;
  /** Clears scatter highlight and selectionStore (banner clear / Escape / x key) */
  onClearPointSelection?: () => void;
  /** Point IDs kept after cut (lasso cut); when set and isCut, only these points are shown */
  cutPointIds?: Accessor<Set<number>>;
  scatterHistoryDataMode?: ScatterHistoryDataMode;
  maneuverTackEntry?: Accessor<'PORT' | 'STBD' | null> | 'PORT' | 'STBD' | null;
  [key: string]: unknown;
}

interface ContainerSize {
  width: number;
  height: number;
}

interface TooltipState {
  visible: boolean;
  content: string;
  x: number;
  y: number;
}

interface Scales {
  xScale: any;
  yScale: any;
}

export default function SimpleScatter(props: SimpleScatterProps) {
  const containerRef: HTMLElement | null = document.getElementById('main-content')
  let chartRef: SVGSVGElement | null = null;
  const [containerSize, setContainerSize] = createSignal<ContainerSize>({ width: 0, height: 0 });
  const [viewportHeight, setViewportHeight] = createSignal(window.innerHeight);
  const [isLoading, setIsLoading] = createSignal(false);
  const [chartData, setChartData] = createSignal<any[] | null>(null);
  const [datasetTimezone, setDatasetTimezone] = createSignal<string | null>(null);

  // Set up timezone from dataset
  createEffect(async () => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    let datasetId: number | null = null;

    if (typeof selectedDatasetId === 'function') {
      const dsId = selectedDatasetId();
      datasetId = dsId ? Number(dsId) : null;
    } else if (selectedDatasetId) {
      datasetId = Number(selectedDatasetId);
    }

    if (className && projectId && datasetId && datasetId > 0) {
      await setCurrentDataset(className, projectId, datasetId);
      const tz = getCurrentDatasetTimezone();
      setDatasetTimezone(tz);
    } else {
      setDatasetTimezone(null);
    }
  });

  // Initialize D3 cleanup
  const { addSelection, addEventListener, addTimer, addObserver, cleanup } = useChartCleanup();

  // Regression calculation state
  const [isCalculatingRegression, setIsCalculatingRegression] = createSignal(false);
  const [regressionResults, setRegressionResults] = createSignal<any>({});
  const [fitData, setFitData] = createSignal<any[]>([]);
  const [fitDataVersion, setFitDataVersion] = createSignal(0);
  // X-axis tick values for the fit table's x-column; kept in sync with the x-axis scale used to draw the fit
  // so the table matches the chart's x-axis (interval ~ (max-min)/10).
  const [fitTableXTicks, setFitTableXTicks] = createSignal<number[]>([]);

  // Store scales for regression drawing
  const [currentScales, setCurrentScales] = createSignal<Scales>({ xScale: null, yScale: null });

  // Enhanced data processing state
  const [isProcessingData, setIsProcessingData] = createSignal(false);
  const [enhancedData, setEnhancedData] = createSignal<any>(null);
  const [dataProcessingStats, setDataProcessingStats] = createSignal<any>(null);
  const [actualRenderedCount, setActualRenderedCount] = createSignal(0);
  const [currentRegressionMethod, setCurrentRegressionMethod] = createSignal("None");
  const [lastProcessedCacheKey, setLastProcessedCacheKey] = createSignal<string | null>(null);
  const [scatterRendered, setScatterRendered] = createSignal(false);

  // Regression calculation state to prevent infinite loops
  const [regressionCalculationId, setRegressionCalculationId] = createSignal(0);

  // Track last processed chart ID to detect chart changes
  let lastProcessedChartId: string | undefined = undefined;
  // Monotonic guard for the async (off-thread) fit recompute: only the latest request draws, so a
  // slower earlier fit can't overwrite a newer one when selections change quickly.
  let fitDrawSeq = 0;
  let lastScatterHistoryOriginalDataRef: unknown[] | undefined = undefined;
  let scatterUPlotHandle: SimpleScatterUPlotMount | null = null;
  let uPlotSourceRows: Record<string, unknown>[] = [];
  let uPlotSeriesConfig: { colorType?: string; color?: string; colorChannel?: { name?: string } } | null = null;

  // Local tooltip state for this component
  const [localTooltip, setLocalTooltip] = createSignal<TooltipState>({
    visible: false,
    content: "",
    x: 0,
    y: 0
  });

  /**
   * Colour-ramp key shown over the plot while this chart is coloured "By Channel". Set from the
   * render (D3 and uPlot alike) because the ramp's extent comes from the same filtered rows the
   * points are drawn from — a legend built from the unfiltered data would name the wrong bounds.
   */
  const [colorChannelLegend, setColorChannelLegend] = createSignal<ColorChannelLegendModel | null>(null);

  // Info icon tooltip state
  const [infoTooltip, setInfoTooltip] = createSignal<TooltipState>({
    visible: false,
    content: "",
    x: 0,
    y: 0
  });

  // Lasso selection: local fallback, or shared across explore/scatter plots via parent props (no selectionStore)
  const [localSelectedPointIds, setLocalSelectedPointIds] = createSignal<Set<number>>(new Set());
  const selectedPointIds = (): Set<number> =>
    props.sharedSelectedPointIds ? props.sharedSelectedPointIds() : localSelectedPointIds();
  const applyLassoSelection = (numericIds: number[]) => {
    if (numericIds.length === 0) {
      return;
    }
    const cutIds = props.cutPointIds?.() ?? new Set<number>();
    const incoming = cutIds.size > 0
      ? numericIds.filter((id) => cutIds.has(id))
      : numericIds;
    if (incoming.length === 0) {
      return;
    }
    const next = new Set([...selectedPointIds(), ...incoming]);
    if (props.onPointSelectionChange) {
      props.onPointSelectionChange(next);
      return;
    }
    setLocalSelectedPointIds(next);
  };
  const clearSelectedPointIds = () => {
    if (props.onClearPointSelection) {
      props.onClearPointSelection();
      return;
    }
    setLocalSelectedPointIds(new Set());
  };
  const [isComputingLassoSelection, setIsComputingLassoSelection] = createSignal(false);
  let lassoWorker: Worker | null = null;
  let isLassoActive = false;
  let isComputingSelection = false;
  let lastLassoInstance: ReturnType<typeof lasso> | null = null;

  const [fitRenderContext, setFitRenderContext] = createSignal<{
    chart: { colorType?: string; colorChannel?: { name?: string }; color?: string };
    regressionMethod: RegressionMethod;
    colortype: string;
    fitSourceData: Array<{ x?: number; y?: number; ID?: number }>;
    preCutFitSourceData: Array<{ x?: number; y?: number; ID?: number; Datetime?: string | Date }>;
    yAxisChannelName: string;
    xywidth: number;
    xyheight: number;
  } | null>(null);

  const getTrendDrawLayer = () => {
    if (!chartRef) {return null;}
    const root = d3.select(chartRef).select("svg").select("g");
    if (root.empty()) {return null;}
    const plotLayer = root.select(".plot-layer");
    return plotLayer.empty() ? root : plotLayer;
  };

  const appendTrendLineSegment = (
    layer: d3.Selection<SVGGElement, unknown, null, undefined>,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: string
  ) => {
    layer.append("line")
      .attr("class", "trend-line-glow")
      .attr("x1", x1)
      .attr("y1", y1)
      .attr("x2", x2)
      .attr("y2", y2);
    layer.append("line")
      .attr("class", "trend-line")
      .attr("x1", x1)
      .attr("y1", y1)
      .attr("x2", x2)
      .attr("y2", y2)
      .style("stroke", color);
  };

  const appendTrendPathSegment = (
    layer: d3.Selection<SVGGElement, unknown, null, undefined>,
    segment: { x: number; y: number }[],
    line: d3.Line<{ x: number; y: number }>,
    color: string
  ) => {
    layer.append("path")
      .datum(segment)
      .attr("class", "trend-line-glow")
      .attr("d", line);
    layer.append("path")
      .datum(segment)
      .attr("class", "trend-line")
      .attr("d", line)
      .style("stroke", color);
  };

  // Function to draw trend lines (defined at component level for reuse across effects)
  const drawTrendLine = (regression: any, color: string, xScale: any, yScale: any) => {
    if (!regression || !chartRef) {return;}

    const chartbody = getTrendDrawLayer();
    if (!chartbody || chartbody.empty()) {return;}

    const { points } = regression;
    const [xMin, xMax] = xScale.domain() as [number, number];
    const [yMin, yMax] = yScale.domain() as [number, number];

    // For linear regression, draw a straight line clipped to axis bounds
    if (regression.slope !== undefined) {
      const clipped = clipLineSegmentToBounds(points[0], points[1], xMin, xMax, yMin, yMax);
      if (!clipped) {return;}

      const [p0, p1] = clipped;
      appendTrendLineSegment(
        chartbody,
        xScale(p0.x),
        yScale(p0.y),
        xScale(p1.x),
        yScale(p1.y),
        color
      );

      // Add R² label for linear regression (position along visible segment)
      const r2Text = regression.r2.toFixed(2);
      const xPosition = xScale(p0.x) + (xScale(p1.x) - xScale(p0.x)) * (2 / 3);
      const yPosition = yScale(p0.y) + (yScale(p1.y) - yScale(p0.y)) * (2 / 3);
      chartbody.append("text")
        .attr("class", "trend-label")
        .attr("x", xPosition + 30)
        .attr("y", yPosition + 30)
        .attr("text-anchor", "middle")
        .attr("font-size", "16px")
        .attr("font-weight", "bold")
        .attr("user-select", "none")
        .attr("pointer-events", "none")
        .attr("fill", color)
        .text(`R² = ${r2Text}`);
    }
    // For LOESS and polynomial regression, clip curve segments to axis bounds
    else {
      const segments = clipPolylineToBounds(points, xMin, xMax, yMin, yMax);
      if (segments.length === 0) {return;}

      const line = d3.line<{ x: number; y: number }>()
        .x((d) => xScale(d.x))
        .y((d) => yScale(d.y))
        .curve(d3.curveCardinal);

      segments.forEach((segment) => {
        if (segment.length < 2) {return;}
        appendTrendPathSegment(chartbody, segment, line, color);
      });
    }
  };

  const applyRegressionDraw = (
    groups: FitGroup[],
    chart: { colorType?: string; colorChannel?: { name?: string }; color?: string },
    colortype: string,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>
  ) => {
    if (!chartRef) {return;}
    const chartbody = getTrendDrawLayer();
    if (!chartbody || chartbody.empty()) {return;}

    chartbody.selectAll(".trend-line-glow").remove();
    chartbody.selectAll(".trend-line").remove();
    chartbody.selectAll(".trend-label").remove();

    groups.forEach((group) => {
      if (!group.regression) {return;}
      const regressionColor = getFitTableLegendColor(chart, colortype, group.color);
      drawTrendLine(group.regression, regressionColor, xScale, yScale);
    });

    const tableFits = buildTableFitsFromGroups(groups, chart, colortype);
    setFitData(tableFits);
    // Match the fit table's x-column to the x-axis: use ~10 ticks from the same scale that drew the axis,
    // so the table spans the visible domain at a ~(max-min)/10 interval.
    setFitTableXTicks(typeof xScale.ticks === "function" ? xScale.ticks(10) : []);
    setFitDataVersion((v) => v + 1);
  };

  // Debounce mechanism
  const [debouncedSelectedRange, setDebouncedSelectedRange] = createSignal(selectedRange());
  const [debouncedCutEvents, setDebouncedCutEvents] = createSignal(cutEvents());
  const [debouncedHasSelection, setDebouncedHasSelection] = createSignal(hasSelection());
  const [debouncedIsCut, setDebouncedIsCut] = createSignal(isCut());
  let updateTimer = null;
  let resizeObserver = null;
  const [zoom, setZoom] = createSignal(false);
  const isZoomed = () => (props.handleZoom ? !!props.zoom : !!zoom());
  let hasSignaledReady = false;
  const signalReadyOnce = () => {
    if (!hasSignaledReady && typeof props.onReady === 'function') {
      hasSignaledReady = true;
      try { props.onReady(); } catch (_) { /* onReady is a best-effort parent callback */ }
    }
  };

  // Enhanced function to process data using enhanced worker manager
  const processDataWithEnhancedWorker = async (data, chart) => {
    if (!data || data.length === 0) {
      setEnhancedData(null);
      setDataProcessingStats(null);
      return;
    }

    setIsProcessingData(true);

    try {
      // Apply chart.filters baseline, then global filters before density optimization
      let filteredData = data;
      const inputCount = data.length;
      const chartConfigFilters = props.chart?.filters;
      const states = selectedStatesTimeseries();
      const races = selectedRacesTimeseries();
      const legs = selectedLegsTimeseries();
      const grades = selectedGradesTimeseries();

      filteredData = applyScatterHistoryClientFilters(data, {
        chartFilters: chartConfigFilters,
        dataMode: props.scatterHistoryDataMode,
        globals: {
          states,
          races,
          legs,
          grades,
        },
        maneuverTackEntry:
          typeof props.maneuverTackEntry === 'function'
            ? props.maneuverTackEntry()
            : props.maneuverTackEntry ?? null,
      });

      log('🔍 SimpleScatter [processDataWithEnhancedWorker]: Applied stacked chart + global filters before density optimization', {
        chartId: chart?.unique_id ?? props.chart?.unique_id,
        inputCount,
        chartFilters: chartConfigFilters,
        filterStates: states,
        filterRaces: races,
        filterLegs: legs,
        filterGrades: grades,
        afterFilter: filteredData.length,
      });

      // Check for selections
      const hasSelections = (debouncedSelectedRange() && debouncedSelectedRange().length > 0) ||
        (selectedEvents() && selectedEvents().length > 0) ||
        (debouncedCutEvents() && debouncedCutEvents().length > 0);

      // Check point count
      const pointCount = filteredData.length;
      const shouldOptimize = pointCount > 5000;
      const hasOptimization = shouldOptimize && !hasSelections; // Only optimize if > 5000 AND no selections
      // Skip optimization if: we have selections (preserve all points) OR we don't need optimization (< 5000 points)
      const skipOptimization = hasSelections || !shouldOptimize;

      const ct = (props.colortype || "DEFAULT") as string;
      let sourceColorMap: Record<string, string> | undefined;
      let dayColorMap: Record<string, string> | undefined;
      let categoryColorMap: Record<string, string> | undefined;
      if (ct === "SOURCE") {
        sourceColorMap = buildSourceColorMapForScatter(filteredData as Record<string, unknown>[]);
      } else if (ct === "DAY") {
        dayColorMap = buildDayColorMapForScatter(filteredData as Record<string, unknown>[]);
      } else if (ct === "EVENT" || ct === "YEAR" || ct === "CONFIG" || ct === "CREW") {
        categoryColorMap = buildExploreCategoryColorMap(filteredData as Record<string, unknown>[], ct);
      }

      const config = {
        xField: 'x',
        yField: 'y',
        colorField: chart.colorType === 'By Channel' && chart.colorChannel ? chart.colorChannel.name : undefined,
        colorType: ct,
        maxPoints: 3000,
        regressionMethod: chart.fitType || 'None',
        tableRange: { min: 6, max: 20, step: 1 },
        skipOptimization,
        sourceColorMap,
        dayColorMap,
        categoryColorMap,
      };


      // Store the regression method for the drawing effect
      setCurrentRegressionMethod(config.regressionMethod);

      // Get chart unique_id
      const chartUniqueId = props.chart?.unique_id || chart.unique_id;

      // Prepare cache parameters
      const cacheParams = {
        className: persistantStore.selectedClassName(),
        sourceId: persistantStore.selectedSourceId(),
        datasetId: String(persistantStore.selectedDatasetId?.() ?? 0),
        projectId: String(persistantStore.selectedProjectId?.() ?? 0),
        chartFilters: chart.filters || [],
        globalFilters: buildScatterGlobalFiltersForCache(props.scatterHistoryDataMode),
        selectionState: {
          selectedRange: debouncedSelectedRange(),
          selectedEvents: selectedEvents(),
          cutEvents: debouncedCutEvents()
        },
        colorType: props.colortype || 'DEFAULT',
        uniqueId: chartUniqueId
      };

      const result = await enhancedScatterWorkerManager.processEnhancedScatterData(filteredData, config, cacheParams);

      // Warn if optimization was expected but didn't reduce point count
      if (hasOptimization && result.optimizationStats.optimizedCount >= result.optimizationStats.originalCount) {
        warn(`[DensityOptimization] Expected optimization for ${pointCount} points but got ${result.optimizationStats.optimizedCount} points (same as original). Performance may be impacted.`);
      }

      setEnhancedData(result);
      setDataProcessingStats({
        originalCount: result.optimizationStats.originalCount,
        optimizedCount: result.optimizationStats.optimizedCount,
        groupsProcessed: result.optimizationStats.groupsProcessed,
        enhancedProcessing: true
      });

      return result;
    } catch (error: any) {
      logError('Error processing data with enhanced worker:', error);
      // Fallback to original data
      setEnhancedData({
        groups: [{
          groupName: 'ALL',
          color: '#1f77b4',
          data: data,
          density: data.length,
          regression: null,
          tableValues: []
        }],
        totalProcessedCount: data.length,
        totalValidDataCount: data.length,
        optimizationStats: {
          originalCount: data.length,
          optimizedCount: data.length,
          groupsProcessed: 1
        }
      });
      setDataProcessingStats(null);
      return {
        groups: [{
          groupName: 'ALL',
          color: '#1f77b4',
          data: data,
          density: data.length,
          regression: null,
          tableValues: []
        }],
        totalProcessedCount: data.length,
        totalValidDataCount: data.length,
        optimizationStats: {
          originalCount: data.length,
          optimizedCount: data.length,
          groupsProcessed: 1
        }
      };
    } finally {
      setIsProcessingData(false);
    }
  };

  // Enhanced data processing function - applies filtering based on chart-specific vs global filters
  const processChartData = () => {
    try {
      if (!props.chart || !props.chart.series || props.chart.series.length === 0) {
        return [];
      }

      const series = props.chart.series[0];

      // Priority: Use series.data if it exists (it's been filtered by applyFiltersToCharts)
      // Only fall back to filtering originalData if series.data doesn't exist
      if (series.data && Array.isArray(series.data)) {
        // Data has been pre-filtered at page level (or is initial data)
        // Selection-based filters (selectedRanges, cutEvents) are handled in the main rendering effect
        log('🔍 SimpleScatter [processChartData]: Using pre-filtered series.data', {
          dataLength: series.data.length,
          originalDataLength: series.originalData?.length || 0,
          chartId: props.chart?.unique_id
        });
        return series.data;
      }

      // Fallback: Use originalData and apply filters
      if (!series.originalData || series.originalData.length === 0) {
        debug('🔍 SimpleScatter: No data available (no series.data or originalData)');
        return [];
      }

      const originalCount = series.originalData.length;

      // Apply filtering logic: chart-specific filters take precedence over global filters
      let filteredData;

      if (props.chart.filters && props.chart.filters.length > 0) {
        // Chart has its own filters - apply only those
        log('🔍 SimpleScatter [processChartData]: Applying chart-specific filters', {
          chartId: props.chart?.unique_id,
          filters: props.chart.filters,
          originalCount,
          beforeFilter: originalCount
        });
        filteredData = filterByTwa(series.originalData, props.chart.filters, [], [], []);
        log('🔍 SimpleScatter [processChartData]: Chart-specific filters applied', {
          chartId: props.chart?.unique_id,
          filters: props.chart.filters,
          beforeFilter: originalCount,
          afterFilter: filteredData.length,
          filteredOut: originalCount - filteredData.length
        });
      } else {
        // No chart-specific filters - ALWAYS apply applyDataFilter
        // (handles both TWA filters AND selectedRanges/time-based selections)
        const states = selectedStatesTimeseries();
        const races = selectedRacesTimeseries();
        const legs = selectedLegsTimeseries();
        const grades = selectedGradesTimeseries();

        log('🔍 SimpleScatter [processChartData]: Applying global filters via applyDataFilter', {
          chartId: props.chart?.unique_id,
          originalCount,
          filterStates: states,
          filterRaces: races,
          filterLegs: legs,
          filterGrades: grades,
          beforeFilter: originalCount
        });

        filteredData = applyDataFilter(series.originalData, states, races, legs, grades);

        log('🔍 SimpleScatter [processChartData]: Global filters applied', {
          chartId: props.chart?.unique_id,
          beforeFilter: originalCount,
          afterFilter: filteredData.length,
          filteredOut: originalCount - filteredData.length,
          filterStates: states,
          filterRaces: races,
          filterLegs: legs,
          filterGrades: grades
        });
      }

      debug('🔍 SimpleScatter: Applied filters to originalData (length:', filteredData.length, ')');
      return filteredData;
    } catch (error: any) {
      logError('Error processing chart data:', error);
      return [];
    }
  };

  // Debounce updates with 250ms delay; when selection/range is cleared, update immediately so cache reflects no selection
  const debounceUpdate = (immediate = false) => {
    if (updateTimer) {
      clearTimeout(updateTimer);
      updateTimer = null;
    }
    const apply = () => {
      setDebouncedSelectedRange(selectedRange());
      setDebouncedCutEvents(cutEvents());
      setDebouncedHasSelection(hasSelection());
      setDebouncedIsCut(isCut());
    };
    if (immediate) {
      apply();
    } else {
      updateTimer = setTimeout(() => {
        apply();
        updateTimer = null;
      }, 250);
    }
  };

  // Escape clears selection when not handled by parent (explore/scatter uses x key + banner via parent)
  createEffect(() => {
    if (props.onClearPointSelection) {return;}
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && selectedPointIds().size > 0) {
        clearSelectedPointIds();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    onCleanup(() => window.removeEventListener("keydown", handleKeyDown));
  });

  type PointOpacityContext = {
    selectedIds: Set<number>;
    lassoHasActiveHighlight: boolean;
    lassoCutActive: boolean;
    lassoHighlightEvents: unknown[];
    lassoHighlightRanges: unknown[];
    legendHighlight: ScatterLegendHighlightState | null;
    chartLegendLabel: string | null;
    twaChannelName: string;
  };

  const getPointOpacity = (
    d: { ID?: number; opacity?: number },
    ctx: PointOpacityContext
  ) => {
    if (ctx.selectedIds.size > 0) {
      const id = d.ID;
      if (id !== undefined && id !== null && ctx.selectedIds.has(Number(id))) {return 1;}
      return 0.05;
    }
    if (ctx.lassoHasActiveHighlight) {
      const inSelection = isScatterPointHighlighted(d as Record<string, unknown>, {
        cutActive: ctx.lassoCutActive,
        selectedEvents: ctx.lassoHighlightEvents,
        selectedRanges: ctx.lassoHighlightRanges,
        legendHighlight: ctx.legendHighlight,
        twaChannelName: ctx.twaChannelName,
        chartLegendLabel: ctx.chartLegendLabel,
      });
      return inSelection ? 0.85 : 0.04;
    }
    if (d.opacity !== undefined) {return 1;}
    return 0.6;
  };

  const buildPointOpacityContext = (): PointOpacityContext => {
    const exploreLassoCut = !!props.onPointSelectionChange;
    const trackStoreHighlight =
      props.enableLasso || props.legendHighlight != null || exploreLassoCut;
    const lassoHighlightEvents = trackStoreHighlight ? selectedEvents() : [];
    const lassoHighlightRanges = trackStoreHighlight ? selectedRanges() : [];
    const lassoCutActive = trackStoreHighlight
      ? exploreLassoCut
        ? untrack(() => (props.cutPointIds?.()?.size ?? 0) > 0) ||
          (isCut() && cutEvents().length > 0)
        : isCut()
      : false;
    const lassoHasActiveHighlight =
      trackStoreHighlight &&
      !lassoCutActive &&
      (lassoHighlightEvents.length > 0 ||
        (hasSelection() && lassoHighlightRanges.length > 0) ||
        props.legendHighlight != null);

    return {
      selectedIds: selectedPointIds(),
      lassoHasActiveHighlight,
      lassoCutActive,
      lassoHighlightEvents,
      lassoHighlightRanges,
      legendHighlight: props.legendHighlight ?? null,
      chartLegendLabel: props.chartLegendLabel ?? null,
      twaChannelName: defaultChannelsStore.twaName(),
    };
  };

  const getPointStrokeWidth = (d: { ID?: number }, selectedIds: Set<number>) => {
    if (selectedIds.size === 0) {return 1;}
    const id = d.ID;
    if (id !== undefined && id !== null && selectedIds.has(Number(id))) {return 2.5;}
    return 1;
  };

  const destroyScatterUPlot = () => {
    if (!scatterUPlotHandle) {return;}
    scatterUPlotHandle.destroy();
    scatterUPlotHandle = null;
  };

  /**
   * Publish (or clear) the colour-ramp legend for the rows about to be drawn.
   *
   * The page-level `colortype` outranks the per-chart setting — it repaints every point by
   * state/day/event/… — so the channel ramp only describes what is on screen while the page is on
   * DEFAULT. Same condition as the channel row in the point tooltip.
   */
  const updateColorChannelLegend = (
    rows: Record<string, unknown>[],
    series: { colorType?: string; colorChannel?: { name?: string } }
  ) => {
    const pageColortype = (props.colortype || "DEFAULT") as string;
    const channelName = series.colorChannel?.name;
    if (pageColortype === "DEFAULT" && series.colorType === "By Channel" && channelName) {
      setColorChannelLegend(buildColorChannelLegendModel(rows, channelName));
    } else {
      setColorChannelLegend(null);
    }
  };

  const buildUPlotDrawPoints = (
    rows: Record<string, unknown>[],
    series: { colorType?: string; color?: string; colorChannel?: { name?: string } }
  ): SimpleScatterUPlotPoint[] => {
    const ct = (props.colortype || "DEFAULT") as string;
    const twaChannelName = defaultChannelsStore.twaName();
    const sourceColorMap = ct === "SOURCE" ? buildSourceColorMapForScatter(rows) : undefined;
    const dayColorMap = ct === "DAY" ? buildDayColorMapForScatter(rows) : undefined;
    const categoryColorMap =
      ct === "EVENT" || ct === "YEAR" || ct === "CONFIG" || ct === "CREW"
        ? buildExploreCategoryColorMap(rows, ct)
        : undefined;
    const yAxisName = (series as { yaxis?: { name?: string } }).yaxis?.name || "y";
    const xAxisName = (series as { xaxis?: { name?: string } }).xaxis?.name || "x";
    const colorChannelScale =
      series.colorType === "By Channel" && series.colorChannel?.name
        ? buildColorChannelScale(rows, series.colorChannel.name)
        : null;
    const opacityCtx = buildPointOpacityContext();
    const cutIds = props.cutPointIds?.() ?? new Set<number>();
    const cutRanges = debouncedCutEvents();
    const cutViewActive = isCutViewActive(cutIds, debouncedIsCut(), cutRanges);

    const out: SimpleScatterUPlotPoint[] = [];
    for (const row of rows) {
      const x = resolveScatterPointX(row, xAxisName);
      const y = resolveScatterPointY(row, yAxisName);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {continue;}
      out.push({
        x,
        y,
        ID: row.ID != null ? Number(row.ID) : undefined,
        color: getScatterPointColor(series, row, {
          colortype: ct,
          colorChannelScale,
          twaChannelName,
          sourceColorMap,
          dayColorMap,
          categoryColorMap,
        }),
        opacity: getPointOpacity(row as { ID?: number; opacity?: number }, opacityCtx),
        visible: !cutViewActive || isPointVisibleInCut(row, cutIds, cutRanges),
      });
    }
    if (cutViewActive && out.length > 0 && !out.some((p) => p.visible)) {
      for (const p of out) {
        p.visible = true;
      }
    }
    return out;
  };

  const setupUPlotLasso = (handle: SimpleScatterUPlotMount) => {
    if (!props.enableLasso && !props.onPointSelectionChange) {return;}
    const overlay = handle.overlay;
    const d3Overlay = d3.select(overlay);
    const lassoInstance = lasso()
      .closePathSelect(true)
      // @ts-expect-error d3-lasso closePathDistance exists at runtime
      .closePathDistance(75)
      .items(d3Overlay.selectAll(".scatter"))
      .targetArea(d3Overlay)
      .skipDragCalculations(true)
      .on("start", () => {
        isLassoActive = true;
        isComputingSelection = false;
        setLocalTooltip({ visible: false, content: "", x: 0, y: 0 });
        setTooltip({ visible: false, content: "", x: 0, y: 0 });
      })
      .on("end", async () => {
        isLassoActive = false;
        const drawnCoords = lassoInstance.getDrawnCoords();
        if (!drawnCoords || drawnCoords.length < 3) {return;}
        isComputingSelection = true;
        setIsComputingLassoSelection(true);
        setSimpleScatterLassoComputingCursor(true);
        try {
          const pxRatio =
            typeof handle.instance.pxRatio === "number" && handle.instance.pxRatio > 0
              ? handle.instance.pxRatio
              : (typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1);
          const series = uPlotSeriesConfig as { xaxis?: { name?: string }; yaxis?: { name?: string } } | null;
          const xName = series?.xaxis?.name || "x";
          const yName = series?.yaxis?.name || "y";
          const rowsForLasso = uPlotSourceRows.map((row) => ({
            ...row,
            x: resolveScatterPointX(row, xName),
            y: resolveScatterPointY(row, yName),
          }));
          const { points } = buildUPlotOverlayLassoPoints(
            rowsForLasso,
            overlay,
            (x, y) => uPlotValToOverlayCoords(handle.instance, x, y, pxRatio),
            (row) => (row.ID != null ? String(row.ID) : null)
          );
          const cutIds = props.cutPointIds?.() ?? new Set<number>();
          const filtered =
            cutIds.size > 0
              ? points.filter((p) => cutIds.has(Number(p.id)))
              : points;
          if (filtered.length === 0) {return;}
          if (!lassoWorker) {
            lassoWorker = new LassoSelectionWorker();
          }
          const messageId = `simple-scatter-uplot-lasso-${Date.now()}-${Math.random()}`;
          const timeoutMs = Math.min(
            120_000,
            Math.max(20_000, 12_000 + filtered.length * 8 + drawnCoords.length * 40)
          );
          const selectedIdStrings = await new Promise<string[]>((resolve, reject) => {
            const timeout = setTimeout(() => {
              lassoWorker?.removeEventListener("message", handleMessage);
              reject(new Error(`Lasso selection computation timeout after ${timeoutMs}ms`));
            }, timeoutMs);
            const handleMessage = (event: MessageEvent) => {
              if (event.data.id !== messageId) {return;}
              clearTimeout(timeout);
              lassoWorker?.removeEventListener("message", handleMessage);
              if (event.data.type === "success" && event.data.result) {
                resolve(event.data.result.selectedIds);
              } else {
                reject(new Error(event.data.error || "Unknown error"));
              }
            };
            lassoWorker.addEventListener("message", handleMessage);
            lassoWorker.postMessage({
              id: messageId,
              type: "COMPUTE_LASSO_SELECTION",
              data: { points: filtered, polygon: drawnCoords },
              timestamp: Date.now(),
            });
          });
          const numericIds = selectedIdStrings.map((id) => Number(id)).filter((id) => !Number.isNaN(id));
          applyLassoSelection(numericIds);
        } catch (error: unknown) {
          logError("Error computing uPlot lasso selection:", error);
        } finally {
          isComputingSelection = false;
          setIsComputingLassoSelection(false);
          setSimpleScatterLassoComputingCursor(false);
        }
      });
    lastLassoInstance = lassoInstance;
    d3Overlay.call(lassoInstance);
  };

  // Update highlight only — avoid full chart rebuild when lasso selection or legend changes
  createEffect(() => {
    props.legendHighlight;
    props.chartLegendLabel;
    props.onPointSelectionChange;
    props.enableLasso;
    selectedEvents();
    selectedRanges();
    hasSelection();
    isCut();
    props.cutPointIds?.();
    const opacityCtx = buildPointOpacityContext();
    if (!chartRef || !scatterRendered()) {return;}
    if (scatterUPlotHandle && uPlotSeriesConfig) {
      scatterUPlotHandle.setPoints(buildUPlotDrawPoints(uPlotSourceRows, uPlotSeriesConfig));
      return;
    }
    d3.select(chartRef)
      .selectAll<SVGCircleElement, { ID?: number; opacity?: number }>(".scatter")
      .style("opacity", (d) => getPointOpacity(d, opacityCtx))
      .style("stroke-width", (d) => getPointStrokeWidth(d, opacityCtx.selectedIds));
  });

  // Rescale axes and reposition points when cut toggles (no full chart rebuild)
  createEffect(() => {
    const cutIds = props.cutPointIds?.() ?? new Set<number>();
    const cutRanges = debouncedCutEvents();
    const cutViewActive = isCutViewActive(cutIds, debouncedIsCut(), cutRanges);
    const ctx = fitRenderContext();
    const rendered = scatterRendered();
    if (!rendered || !chartRef) {return;}
    if (scatterUPlotHandle && uPlotSeriesConfig) {
      scatterUPlotHandle.setPoints(buildUPlotDrawPoints(uPlotSourceRows, uPlotSeriesConfig));
      return;
    }
    if (!ctx) {return;}

    const chartbody = d3.select(chartRef).select<SVGGElement>("g");
    if (chartbody.empty()) {return;}

    chartbody
      .selectAll<SVGCircleElement, Record<string, unknown>>(".scatter")
      .style("display", (d) => {
        if (!cutViewActive) {return null;}
        const visible = filterPointsByCut(
          [d as { ID?: number; Datetime?: string | Date }],
          true,
          cutIds,
          cutRanges
        ).length > 0;
        return visible ? null : "none";
      })
      .style("opacity", (d) => {
        if (!cutViewActive) {return null;}
        const visible = filterPointsByCut(
          [d as { ID?: number; Datetime?: string | Date }],
          true,
          cutIds,
          cutRanges
        ).length > 0;
        return visible ? 1 : null;
      });

    const scaleSource = getScaleSourceForCut(
      ctx.preCutFitSourceData,
      cutViewActive,
      cutIds,
      cutRanges
    );
    if (scaleSource.length < 1) {
      if (cutViewActive) {
        setCurrentScales({ xScale: null, yScale: null });
      }
      return;
    }

    const { xScale, yScale } = buildScatterScales(
      scaleSource,
      scaleSource as Record<string, unknown>[],
      ctx.yAxisChannelName,
      ctx.xywidth,
      ctx.xyheight
    );
    setCurrentScales({ xScale, yScale });

    chartbody.selectAll<SVGGElement, unknown>("g.axes").each(function () {
      const axisGroup = d3.select(this);
      const transform = axisGroup.attr("transform") || "";
      if (transform.includes(String(ctx.xyheight))) {
        axisGroup.call(d3.axisBottom(xScale).ticks(5).tickFormat(myTickFormat));
      } else {
        axisGroup.call(d3.axisLeft(yScale).ticks(5).tickFormat(myTickFormat));
      }
    });

    chartbody.selectAll<SVGGElement, unknown>("g.grid").each(function () {
      const gridGroup = d3.select(this);
      const transform = gridGroup.attr("transform") || "";
      if (transform.includes(String(ctx.xyheight))) {
        gridGroup.call(
          d3.axisBottom(xScale).ticks(5).tickFormat(() => "").tickSize(-ctx.xyheight)
        );
      } else {
        gridGroup.call(
          d3.axisLeft(yScale).ticks(5).tickFormat(() => "").tickSize(-ctx.xywidth)
        );
      }
    });

    chartbody
      .selectAll<SVGCircleElement, Record<string, unknown>>(".scatter")
      .attr("cx", (d) => xScale(Number(d.x)))
      .attr("cy", (d) => yScale(resolveScatterPointY(d, ctx.yAxisChannelName)));
  });

  // Recompute and redraw fit when lasso selection or cut changes (no full chart rebuild)
  createEffect(() => {
    const selectedIds = selectedPointIds();
    const cutIds = props.cutPointIds?.() ?? new Set<number>();
    const cutRanges = debouncedCutEvents();
    const cutViewActive = isCutViewActive(cutIds, debouncedIsCut(), cutRanges);
    const ctx = fitRenderContext();
    const scales = currentScales();
    if (!scatterRendered() || !ctx || !chartRef) {return;}
    if (scatterUPlotHandle) {return;}
    if (ctx.regressionMethod === "None") {return;}
    if (!scales.xScale || !scales.yScale) {return;}

    let sourceData = ctx.fitSourceData;
    if (cutViewActive) {
      sourceData = filterPointsByCut(sourceData, true, cutIds, cutRanges);
    }
    // Cut + lasso: fit lasso subset; cut only: fit all cut points; lasso only: fit selection
    const fitSelectedIds = selectedIds;

    // Off-thread fit: capture scales + a sequence token now (reactive reads already tracked above),
    // then draw when the pool resolves — but only if no newer recompute has superseded this one.
    const mySeq = ++fitDrawSeq;
    const xScaleRef = scales.xScale;
    const yScaleRef = scales.yScale;
    computeFitGroups(
      sourceData,
      fitSelectedIds,
      ctx.chart,
      ctx.regressionMethod,
      ctx.colortype,
      buildFitTableRange(xScaleRef)
    )
      .then((groups) => {
        if (mySeq !== fitDrawSeq) {return;} // superseded by a newer selection/cut change
        applyRegressionDraw(groups, ctx.chart, ctx.colortype, xScaleRef, yScaleRef);
      })
      .catch(() => {
        /* fit computation failed; keep the previously drawn fit rather than clearing it */
      });
  });

  // Watch for chart changes and fetch data
  createEffect(() => {
    // Explicitly track the chart prop to ensure reactivity
    const chart = props.chart;
    const series = chart?.series?.[0];
    // Use axis names as primary identity so switching scatter objects (different x/y axes) clears state.
    // unique_id from API can be the same across different scatter charts.
    const chartId = (series?.xaxis?.name && series?.yaxis?.name)
      ? `${series.xaxis.name}_${series.yaxis.name}`
      : (chart?.unique_id || 'unknown');

    // CRITICAL: Clear all data state when chart changes to prevent stale data
    // This ensures we don't show data from the previous chart
    if (chartId !== lastProcessedChartId && lastProcessedChartId !== undefined) {
      debug('🔍 SimpleScatter: Chart changed, clearing previous data', {
        oldChartId: lastProcessedChartId,
        newChartId: chartId
      });
      setChartData(null);
      setEnhancedData(null);
      setDataProcessingStats(null);
      setRegressionResults({});
      setFitData([]);
      setFitTableXTicks([]);
      setFitDataVersion(prev => prev + 1);
      setActualRenderedCount(0);
      setScatterRendered(false);
      if (!props.setSharedSelectedPointIds) {
        clearSelectedPointIds();
      }
    }

    // Update last processed chart ID
    lastProcessedChartId = chartId;

    if (chart && chart.series && chart.series.length > 0) {
      const series = chart.series[0];
      // Explicitly track data and originalData arrays to ensure reactivity
      // Accessing .length ensures we track array changes
      const seriesDataLength = series?.data?.length ?? 0;
      const originalDataLength = series?.originalData?.length ?? 0;
      // Track the data array reference itself to detect when it changes
      const seriesDataRef = series?.data;
      const originalDataRef = series?.originalData;

      debug('🔍 SimpleScatter: Chart data effect triggered', {
        chartId: chart.unique_id || chartId,
        seriesDataLength,
        originalDataLength,
        dataIsOriginal: seriesDataRef === originalDataRef,
        hasData: !!seriesDataRef,
        hasOriginalData: !!originalDataRef
      });

      if (
        props.scatterHistoryDataMode &&
        originalDataRef &&
        originalDataRef !== lastScatterHistoryOriginalDataRef
      ) {
        if (lastScatterHistoryOriginalDataRef !== undefined) {
          setEnhancedData(null);
          setLastProcessedCacheKey(null);
        }
        lastScatterHistoryOriginalDataRef = originalDataRef as unknown[];
      }

      // Process chart data (will use series.data if it's been pre-filtered, otherwise filter originalData)
      const data = processChartData();
      debug('🔍 SimpleScatter: Processed chart data length:', data.length);
      setChartData(data);
    } else {
      // No chart data - clear state
      setChartData(null);
    }
  });

  // DISABLED: Watch for filter changes and re-process data
  // Filters now only apply when Apply button is clicked in PageSettings
  // This prevents immediate updates when filters are changed in the UI
  /*
  createEffect(() => {
    if (props.chart && props.chart.series && props.chart.series.length > 0) {
      // Track all filter signals to ensure reactivity
      const states = selectedStatesTimeseries();
      const races = selectedRacesTimeseries();
      const legs = selectedLegsTimeseries();
      const grades = selectedGradesTimeseries();
      
      // Re-process data when filters change (this will trigger regression recalculation)
      const data = processChartData();
      setChartData(data);
    }
  });
  */


  // Watch for selection changes and debounce them
  // Set up resize observer for container size changes and window resize listener
  createEffect(() => {
    // Wait for chartRef to be available
    if (!chartRef) {
      debug('🔍 SimpleScatter: Resize observer effect - chartRef not available yet');
      return;
    }

    debug('🔍 SimpleScatter: Setting up ResizeObserver and window resize listener');

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const newWidth = entry.contentRect.width;
          const newHeight = entry.contentRect.height;
          debug('🔍 SimpleScatter: ResizeObserver detected size change:', {
            width: newWidth,
            height: newHeight
          });
          setContainerSize({
            width: newWidth,
            height: newHeight
          });
        }
      });
      resizeObserver.observe(chartRef);
      debug('🔍 SimpleScatter: ResizeObserver attached to chartRef');

      // Set initial size
      const initialWidth = chartRef.clientWidth;
      const initialHeight = chartRef.clientHeight;
      debug('🔍 SimpleScatter: Setting initial container size:', {
        width: initialWidth,
        height: initialHeight
      });
      setContainerSize({
        width: initialWidth,
        height: initialHeight
      });
    } else {
      warn('🔍 SimpleScatter: ResizeObserver not supported in this browser');
    }

    // Add window resize listener for viewport height changes
    // Only update if chartRef is actually mounted and visible
    const handleWindowResize = () => {
      const newHeight = window.innerHeight;

      // Only update if chartRef exists and is visible (has dimensions)
      if (!chartRef) {return;}

      const newWidth = chartRef.clientWidth;
      const newContainerHeight = chartRef.clientHeight;

      // Only log and update if dimensions are valid
      if (newWidth > 0 || newContainerHeight > 0) {
        debug('🔍 SimpleScatter: Window resize detected, new viewport height:', newHeight);
        debug('🔍 SimpleScatter: Updating container size on window resize:', {
          width: newWidth,
          height: newContainerHeight
        });
        setViewportHeight(newHeight);
        setContainerSize({
          width: newWidth,
          height: newContainerHeight
        });
      }
      // Silently skip if dimensions are 0 (component not visible/mounted)
    };
    window.addEventListener('resize', handleWindowResize);
    debug('🔍 SimpleScatter: Window resize listener attached');

    // onCleanup, not a returned function: Solid discards createEffect return values, so the observer
    // and listener used to outlive the component. This effect reads nothing tracked (only the
    // chartRef local), so it runs once and the cleanup fires only at unmount.
    onCleanup(() => {
      debug('🔍 SimpleScatter: Cleaning up ResizeObserver and window resize listener');
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleWindowResize);
    });
  });

  createEffect(() => {
    const range = selectedRange();
    const ranges = selectedRanges();
    const cuts = cutEvents();
    const hasSel = hasSelection();
    const isCutVal = isCut();
    props.cutPointIds?.();

    debounceUpdate(true);

    if (!persistantStore.filterChartsBySelection()) {return;}

    try {
      log('SimpleScatter: Selection effect triggered', {
        rangeLen: range?.length || 0,
        rangesLen: ranges?.length || 0,
        cutsLen: cuts?.length || 0,
        hasSel,
        isCutVal
      });
    } catch { /* debug logging only; never let it break the effect */ }

    props.chart;

    const rangeCleared = (!range || range.length === 0) && (!ranges || ranges.length === 0);
    const cutStillActive =
      (props.cutPointIds?.()?.size ?? 0) > 0 || (isCutVal && cuts.length > 0);
    // Clearing a lasso highlight must not drop the density cache while a cut is still
    // showing — that forced a full rebuild of every point, then the cut hide ran after.
    if (rangeCleared && !cutStillActive) {
      setEnhancedData(null);
      setLastProcessedCacheKey(null);
    }
  });

  let chartRenderGeneration = 0;

  // Main chart rendering effect - only responds to debounced values
  createEffect(async () => {
    // Make sure we're reactive to chart changes, container size, viewport height, color type, and zoom state
    // NOTE: Scale factor changes are handled by CSS transform only - we don't want to redraw on scale changes
    // NOTE: Filters are now only applied when Apply button is clicked, so we don't watch filterState here
    const currentChart = props.chart;
    const size = containerSize(); // Track container size changes
    const vh = viewportHeight(); // Track viewport height changes
    const fetchedData = chartData(); // Track chart data changes
    const currentRegressionResults = regressionResults(); // Track regression results changes
    const currentColorType = props.colortype; // Track color type changes
    const zoomed = isZoomed(); // Track zoom state changes
    props.scatterHistoryDataMode;
    if (props.scatterHistoryDataMode) {
      startDate();
      endDate();
    }
    if (typeof props.maneuverTackEntry === 'function') {
      props.maneuverTackEntry();
    }
    const exploreLassoCut = !!props.onPointSelectionChange;
    props.legendHighlight;
    props.legendFilterGroup;
    props.chartLegendLabel;
    // Selection state is read for the initial paint only, NOT tracked: lasso/cut changes are
    // handled by the incremental effects above (opacity, cut hide+rescale, fit redraw). Tracking
    // them here caused a full teardown+rebuild on every selection change, drawn on top of the
    // incremental updates — the "all points flash, then the cut" the explore pages showed.
    const pointOpacityCtx = untrack(() => buildPointOpacityContext());

    // Get current filter state for cache key calculation (but don't react to changes)
    // Filters are applied at the page level before data reaches SimpleScatter
    const filterState = buildScatterGlobalFiltersForCache(props.scatterHistoryDataMode);

    // Track chart-specific filters to ensure reactivity
    const chartFilters = currentChart?.filters || [];

    if (!currentChart || !currentChart.series || currentChart.series.length === 0) {
      debug('🔍 SimpleScatter: No chart or series data available');
      setColorChannelLegend(null);
      return;
    }

    const chart = currentChart.series[0];
    let data: Record<string, unknown>[];
    if (fetchedData != null) {
      data = fetchedData as Record<string, unknown>[];
    } else {
      const source = (chart.originalData ?? chart.data ?? []) as Record<string, unknown>[];
      data = applyScatterHistoryClientFilters(source, {
        chartFilters,
        dataMode: props.scatterHistoryDataMode,
        globals: filterState,
        maneuverTackEntry:
          typeof props.maneuverTackEntry === 'function'
            ? props.maneuverTackEntry()
            : props.maneuverTackEntry ?? null,
      });
    }

    // Prefer current chart's series data so we never render with stale data when switching charts.
    // We'll define renderData after checking for cache and processing data

    // If colorType is By Channel and colorChannel data is not in the data, fetch it
    if (chart.colorType === 'By Channel' && chart.colorChannel && chart.colorChannel.name) {
      // Check if colorChannel data is already present
      const hasColorChannelData = data.length > 0 && data[0][chart.colorChannel.name] !== undefined;

      if (!hasColorChannelData) {
        // For now, we'll assume the data needs to be fetched
        // In a real implementation, this would trigger a data fetch
      }
    }

    // Get regression method; only show when explicitly specified in chart config
    const regressionMethod = (() => {
      const configured = chart.fitType;
      // Only show regression if explicitly specified in chart configuration
      if (configured && configured !== "None") {
        return configured;
      }
      return "None"; // No regressions unless explicitly configured
    })(); // Options: "None", "Linear", "Poly 2", "Poly 3", "Loess"

    // Selection values for the initial paint — untracked for the same reason as above.
    const {
      currentSelectedRange,
      currentCutEvents,
      currentHasSelection,
      currentIsCut,
      currentCutPointIds,
    } = untrack(() => ({
      currentSelectedRange: debouncedSelectedRange(),
      currentCutEvents: debouncedCutEvents(),
      currentHasSelection: debouncedHasSelection(),
      currentIsCut: debouncedIsCut(),
      currentCutPointIds: props.cutPointIds?.() ?? new Set<number>(),
    }));

    const xymargin = { top: 20, right: 30, bottom: 50, left: 60 }; // Margins

    // Get actual dimensions - prefer containerSize from ResizeObserver (most reliable)
    // but fall back to direct measurement if needed
    const containerSizeData = size;
    let refWidth = containerSizeData.width > 0
      ? containerSizeData.width
      : (chartRef?.clientWidth || chartRef?.offsetWidth || 0);
    let refHeight = containerSizeData.height > 0
      ? containerSizeData.height
      : (chartRef?.clientHeight || chartRef?.offsetHeight || 0);

    // If dimensions are still 0, defer measurement to avoid forced reflow
    // Use defaults and let the next render cycle handle it properly
    // This avoids the forced reflow warning - dimensions will be correct on next render

    // If still 0, try to get dimensions from parent container or use defaults
    if (refWidth <= 0 || refHeight <= 0) {
      if (chartRef?.parentElement) {
        const parent = chartRef.parentElement;
        refWidth = parent.clientWidth || refWidth;
        refHeight = parent.clientHeight || refHeight;
      }

      // Last resort: use reasonable defaults if container still has no dimensions
      // This can happen during initial render before layout is complete
      if (refWidth <= 0) {
        refWidth = containerRef?.clientWidth || window.innerWidth * 0.8 || 800;
      }
      if (refHeight <= 0) {
        refHeight = 400; // Default height for probability charts
      }
    }

    let xywidth = refWidth - xymargin.left - xymargin.right;
    let xyheight = 400 - xymargin.top - xymargin.bottom;

    if (isZoomed()) {
      // When zoomed, use container's height but cap to a reasonable max to avoid overflows
      xywidth = refWidth - xymargin.left - xymargin.right;
      const fallbackZoomHeight = 700;
      const containerHeight = refHeight || fallbackZoomHeight;
      const cappedHeight = Math.min(containerHeight, 700);
      xyheight = cappedHeight - xymargin.top - xymargin.bottom;
    } else if (props.class_name == 'col1') {
      xywidth = refWidth - xymargin.left - xymargin.right;

      // If there's only one chart, use 75% of viewport height
      if (props.totalCharts === 1) {
        const scaledHeight = vh * 0.75;
        xyheight = scaledHeight - xymargin.top - xymargin.bottom;
      } else {
        // Smart scaling: use 75% of viewport height, with min 400px and max 1200px
        const scaledHeight = Math.max(400, Math.min(1200, vh * 0.75));
        xyheight = (refHeight || scaledHeight) - xymargin.top - xymargin.bottom;
      }
    } else if (props.class_name == 'col2') {
      xywidth = refWidth - xymargin.left - xymargin.right
      xyheight = singleRowHeightPx(COL2_ROW_HEIGHT_PX, vh) - xymargin.top - xymargin.bottom;
    } else if (props.class_name == 'col2x2') {
      // Four charts as two rows of two. Both rows have to be on the page at once, so each cell
      // takes a share of the viewport rather than a fixed target.
      xywidth = refWidth - xymargin.left - xymargin.right
      xyheight = col2x2CellHeightPx(vh) - xymargin.top - xymargin.bottom;
    } else if (props.class_name == 'col3') {
      // Three charts in a single row. Height is a target, not the measured host: the host is
      // `height: auto` with a 400px min-height floor, so reading refHeight back just returned the
      // floor (squat row, dead space below) and scaling it would feed the ResizeObserver a taller
      // SVG on every pass.
      xywidth = refWidth - xymargin.left - xymargin.right
      xyheight = singleRowHeightPx(COL3_ROW_HEIGHT_PX, vh) - xymargin.top - xymargin.bottom;
    } else if (props.class_name == 'col3x') {
      // Multi-row three-column grid. Same idea as col2x2: each row takes a share of the
      // viewport, so a 2x3 grid fills the page and taller grids scroll.
      xywidth = refWidth - xymargin.left - xymargin.right
      xyheight = col3xCellHeightPx(vh) - xymargin.top - xymargin.bottom;
    }

    // Validate dimensions before proceeding - if invalid, skip rendering
    if (!chartRef || xywidth <= 0 || xyheight <= 0) {
      debug('🔍 SimpleScatter: Invalid chart dimensions, skipping render', {
        chartRef: !!chartRef,
        xywidth,
        xyheight,
        refWidth,
        refHeight
      });
      signalReadyOnce();
      return;
    }

    const useUPlotNow = shouldUseSimpleScatterUPlot(props.useUPlot, data.length);
    const renderGeneration = ++chartRenderGeneration;

    if (useUPlotNow) {
      if (renderGeneration !== chartRenderGeneration) {return;}
      destroyScatterUPlot();
      d3.select(chartRef).selectAll("*").remove();
      uPlotSourceRows = (data as Record<string, unknown>[]).slice();
      uPlotSeriesConfig = chart;
      updateColorChannelLegend(uPlotSourceRows, chart);
      const axisColor = isDark() ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.65)";
      const gridColor = isDark() ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)";
      const computedW = xywidth + xymargin.left + xymargin.right;
      const computedH = xyheight + xymargin.top + xymargin.bottom;
      chartRef.style.width = `${computedW}px`;
      chartRef.style.height = `${computedH}px`;
      scatterUPlotHandle = mountSimpleScatterUPlot(chartRef, {
        width: computedW,
        height: computedH,
        padding: [xymargin.top, xymargin.right, xymargin.bottom, xymargin.left],
        xLabel: String(chart.xaxis?.name || ""),
        yLabel: String(chart.yaxis?.name || ""),
        axisColor,
        gridColor,
        points: buildUPlotDrawPoints(uPlotSourceRows, chart),
      });
      setupUPlotLasso(scatterUPlotHandle);
      setScatterRendered(true);
      setActualRenderedCount(uPlotSourceRows.length);
      signalReadyOnce();
      debug("SimpleScatter: rendered with uPlot", { points: uPlotSourceRows.length });
      return;
    }

    destroyScatterUPlot();

    const make_x_gridlines = (xScale) => {
      return d3.axisBottom(xScale).ticks(5).tickFormat(myTickFormat)
    }

    const make_y_gridlines = (yScale) => {
      return d3.axisLeft(yScale).ticks(5).tickFormat(myTickFormat)
    }

    // Check if we already have enhanced data - if so, skip processing
    const currentEnhancedResult = enhancedData();

    // Compute current cache key using the same algorithm as the worker manager
    // Use axis names as primary chart identity so different scatter objects (different x/y axes) never share cache.
    // unique_id from API can be the same across different scatter charts, causing wrong data to persist after switching.
    const chartUniqueId = (chart.xaxis?.name && chart.yaxis?.name)
      ? `${chart.xaxis.name}_${chart.yaxis.name}`
      : (currentChart?.unique_id ?? undefined);
    // Only include selection in cache key when "Filter by selection" is on; otherwise selection changes won't trigger re-process
    const filterBySelection = persistantStore.filterChartsBySelection();
    // Untracked: when "Filter by selection" is on, selection changes arrive via props.chart
    // (the page refilters and calls setCharts), so tracking these signals here only added
    // redundant full rebuilds.
    const selectionStateForCache = filterBySelection
      ? untrack(() => ({ selectedRange: debouncedSelectedRange(), selectedEvents: selectedEvents(), cutEvents: debouncedCutEvents() }))
      : { selectedRange: [], selectedEvents: [], cutEvents: [] };
    const currentCacheKey = createDensityOptimizationCacheKey(
      persistantStore.selectedClassName() || '',
      persistantStore.selectedSourceId() || '',
      currentColorType || 'DEFAULT',
      regressionMethod,
      chartFilters,
      filterState,
      selectionStateForCache,
      chartUniqueId
    );
    const cacheKeyChanged = lastProcessedCacheKey() !== null && lastProcessedCacheKey() !== currentCacheKey;

    // Only process if we don't have enhanced data yet OR if cache key actually changed (not on first load)
    // ALSO check if we're not already processing to prevent race conditions
    let finalEnhancedResult = null;
    if ((!currentEnhancedResult || cacheKeyChanged) && data.length > 0 && !isProcessingData()) {
      // Reset scatter rendered flag since we're processing new data
      setScatterRendered(false);

      // Await the processing to ensure we have the data before rendering
      const result = await processDataWithEnhancedWorker(data, chart);
      setEnhancedData(result);
      setLastProcessedCacheKey(currentCacheKey);

      // Use the result directly instead of reading from signal
      finalEnhancedResult = result;

      // Signal readiness so parent can hide loading
      signalReadyOnce();
    } else {
      // Use existing enhanced data
      finalEnhancedResult = currentEnhancedResult;
    }

    if (renderGeneration !== chartRenderGeneration) {
      return;
    }

    // CRITICAL: Never render without optimized data - wait for it
    if (!finalEnhancedResult && data.length > 0) {
      return;
    }

    if (renderGeneration !== chartRenderGeneration) {
      return;
    }

    // At this point, we MUST have enhanced data
    if (!finalEnhancedResult) {
      // Nothing to draw (no data and no cached result): clear the stale chart the way the
      // old pre-await wipe did, so an emptied dataset doesn't leave old points on screen.
      setScatterRendered(false);
      d3.select(chartRef).selectAll("*").remove();
      return;
    }

    // Data is ready and this pass is still the newest: now tear down and rebuild the SVG.
    setScatterRendered(false);
    d3.select(chartRef).selectAll("*").remove();

    const svg = d3
      .select(chartRef)
      .append("svg")
      .attr("width", xywidth + xymargin.left + xymargin.right)
      .attr("height", xyheight + xymargin.top + xymargin.bottom)
      .on("dblclick", () => {
        // If parent provided a zoom handler, delegate like PerfScatter
        if (props.handleZoom) {
          let info = props.chart || null;
          if (props.zoom) { // if already zoomed, dblclick should exit
            info = null;
          }
          props.handleZoom(info);
        } else {
          // Fallback to local zoom behavior
          setZoom(!zoom());
        }
      });

    addSelection(svg);

    const chartbody = svg
      .append("g")
      .attr("transform", `translate(${xymargin.left}, ${xymargin.top})`);

    addSelection(chartbody);

    // Determine renderData from enhanced result ONLY
    const renderData = finalEnhancedResult.groups.flatMap(group => group.data);


    // Now apply range/cut filtering to renderData only when "Filter by selection" is enabled
    let filteredData = renderData;
    const renderDataCount = renderData.length;

    log('🔍 SimpleScatter [render]: Starting range/cut filtering', {
      chartId: currentChart?.unique_id,
      renderDataCount,
      filterBySelection,
      hasSelection: currentHasSelection,
      selectedRangeCount: currentSelectedRange?.length || 0,
      isCut: currentIsCut,
      cutEventsCount: currentCutEvents?.length || 0
    });

    if (filterBySelection) {
      // Apply range filtering if there's a selection
      if (currentHasSelection && currentSelectedRange.length > 0) {
        const rangeItem = currentSelectedRange[0];
        if (rangeItem.start_time && rangeItem.end_time) {
          const startTime = new Date(rangeItem.start_time);
          const endTime = new Date(rangeItem.end_time);

          log('🔍 SimpleScatter [render]: Applying selectedRange filter', {
            chartId: currentChart?.unique_id,
            beforeFilter: renderDataCount,
            startTime: startTime.toISOString(),
            endTime: endTime.toISOString()
          });

          filteredData = renderData.filter(d => {
            const datetime = new Date(d.Datetime);
            return datetime >= startTime && datetime <= endTime;
          });

          log('🔍 SimpleScatter [render]: SelectedRange filter applied', {
            chartId: currentChart?.unique_id,
            beforeFilter: renderDataCount,
            afterFilter: filteredData.length,
            filteredOut: renderDataCount - filteredData.length
          });
        }
      } else {
        log('🔍 SimpleScatter [render]: No range filtering applied, using all renderData', {
          chartId: currentChart?.unique_id,
          renderDataCount
        });
      }
    } else {
      log('🔍 SimpleScatter [render]: Filter by selection is off, using all renderData', {
        chartId: currentChart?.unique_id,
        renderDataCount
      });
    }

    const preCutFilteredData = filteredData;

    // Explore keeps the full point set in the DOM so toggling cut can hide/show without a
    // rebuild. Still treat cut as active here so the first paint uses cut scales and hides
    // non-cut points immediately (otherwise a rebuild flashes the complete dataset).
    const exploreCutViewActive = isCutViewActive(
      currentCutPointIds,
      currentIsCut,
      currentCutEvents
    );
    const cutViewActive = exploreLassoCut
      ? false
      : exploreCutViewActive;
    if (cutViewActive) {
      const beforeCutFilter = filteredData.length;
      if (currentCutPointIds.size > 0) {
        filteredData = filterScatterPointsByIds(filteredData, currentCutPointIds);
      } else {
        filteredData = filterPointsByCut(
          filteredData,
          true,
          currentCutPointIds,
          currentCutEvents
        );
      }
      log('🔍 SimpleScatter [render]: Cut filter applied', {
        chartId: currentChart?.unique_id,
        beforeFilter: beforeCutFilter,
        afterFilter: filteredData.length,
        cutByPointIds: currentCutPointIds.size > 0,
        cutEventsCount: currentCutEvents.length,
      });
    }

    // Keep every filtered point on screen. Legend/lasso selection dims non-selected
    // points via getPointOpacity instead of isolating the group.
    const displayData = filteredData;

    const yAxisChannelName = chart.yaxis?.name || "y";
    const resolvePointY = (d: Record<string, unknown>): number =>
      resolveScatterPointY(d, yAxisChannelName);

    const scaleCutActive = cutViewActive || (exploreLassoCut && exploreCutViewActive);
    const scaleSource = getScaleSourceForCut(
      preCutFilteredData,
      scaleCutActive,
      currentCutPointIds,
      currentCutEvents
    );
    const xScaleSource = scaleCutActive ? scaleSource : renderData;
    const yScaleSource = scaleCutActive
      ? (scaleSource as Record<string, unknown>[])
      : [
          ...(data as Record<string, unknown>[]),
          ...(filteredData as Record<string, unknown>[]),
        ];

    const { xScale, yScale } = buildScatterScales(
      xScaleSource,
      yScaleSource,
      yAxisChannelName,
      xywidth,
      xyheight
    );

    // Store scales for regression drawing
    setCurrentScales({ xScale, yScale });

    // Color channel scale for By Channel colorType
    const colorChannelScale = buildColorChannelScale(
      filteredData as Record<string, unknown>[],
      chart.colorType === 'By Channel' && chart.colorChannel?.name ? chart.colorChannel.name : undefined
    );
    updateColorChannelLegend(filteredData as Record<string, unknown>[], chart);

    const exploreCt = (props.colortype || "DEFAULT") as string;
    const sourceColorMapForDraw =
      exploreCt === "SOURCE"
        ? buildSourceColorMapForScatter(filteredData as Record<string, unknown>[])
        : undefined;
    const dayColorMapForDraw =
      exploreCt === "DAY"
        ? buildDayColorMapForScatter(filteredData as Record<string, unknown>[])
        : undefined;
    const categoryColorMapForDraw =
      exploreCt === "EVENT" || exploreCt === "YEAR" || exploreCt === "CONFIG" || exploreCt === "CREW"
        ? buildExploreCategoryColorMap(filteredData as Record<string, unknown>[], exploreCt)
        : undefined;

    // Add Y-axis
    const yAxis = chartbody.append("g")
      .attr("class", "axes")
      .attr("transform", "translate(0, 0)")
      .call(d3.axisLeft(yScale).ticks(5).tickFormat(myTickFormat));
    addSelection(yAxis);

    // Add X-axis
    const xAxis = chartbody.append("g")
      .attr("class", "axes")
      .attr("transform", "translate(0," + xyheight + ")")
      .call(d3.axisBottom(xScale).ticks(5).tickFormat(myTickFormat));
    addSelection(xAxis);

    // Add Axis labels
    chartbody
      .append("text")
      .attr("class", "y-label chart-element y-label--start simple-scatter-y-label")
      .attr("text-anchor", "start")
      .attr("x", 60)
      .attr("y", 10)
      .text(chart.yaxis.name || "Y-Axis");

    // X-axis label. `.x-label.chart-element` is styled `dominant-baseline: hanging`, so the glyphs
    // render BELOW this anchor, not above it — at 10px above the SVG's bottom edge the label hung
    // past the box and its lower half was clipped away. Clear the edge by more than a line height.
    chartbody
      .append("text")
      .attr("class", "x-label chart-element")
      .attr("text-anchor", "middle")
      .attr("transform", `translate(${xywidth / 2}, ${xyheight + xymargin.bottom - X_LABEL_HANGING_CLEARANCE_PX})`)
      .attr("font-size", "20px")
      .text(chart.xaxis.name || "X-Axis");

    // Add background grid lines
    const xGrid = chartbody.append("g")
      .attr("class", "grid")
      .attr("transform", "translate(0," + xyheight + ")")
      .style("stroke-dasharray", ("3,3"))
      .call(make_x_gridlines(xScale)
        .tickSize(-xyheight)
        .tickFormat(""));
    addSelection(xGrid);

    const yGrid = chartbody.append("g")
      .attr("class", "grid")
      .attr("transform", "translate(0, 0)")
      .style("stroke-dasharray", ("3,3"))
      .call(make_y_gridlines(yScale)
        .tickSize(-xywidth)
        .tickFormat(""));
    addSelection(yGrid);

    const scatterClipId = `scatter-clip-${String(chart.xaxis?.name || "x").replace(/[^a-zA-Z0-9_-]/g, "_")}-${String(chart.yaxis?.name || "y").replace(/[^a-zA-Z0-9_-]/g, "_")}`;
    chartbody.append("defs")
      .append("clipPath")
      .attr("id", scatterClipId)
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", xywidth)
      .attr("height", xyheight);
    const plotLayer = chartbody
      .append("g")
      .attr("class", "plot-layer")
      .attr("clip-path", `url(#${scatterClipId})`);

    function getColor(series, d) {
      return getScatterPointColor(series, d as Record<string, unknown>, {
        colortype: (props as { colortype?: string }).colortype || "DEFAULT",
        colorChannelScale,
        twaChannelName: defaultChannelsStore.twaName(),
        sourceColorMap: sourceColorMapForDraw,
        dayColorMap: dayColorMapForDraw,
        categoryColorMap: categoryColorMapForDraw,
      });
    }


    const renderSelectedIds = selectedPointIds();

    const shouldDisableHover = () => isLassoActive || isComputingSelection;

    const mouseover = (event, d) => {
      if (shouldDisableHover()) {return;}
      const tooltipContent = getTooltipContent(d);

      // Get the scale factor from CSS custom property
      const scaleFactor = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--scale-factor')) || 1;

      // Left-biased beside the cursor, flipped to the cursor's left near the right viewport edge so
      // the rightmost column of a chart grid doesn't hide its tooltip off screen.
      const { x: adjustedX, y: adjustedY } = computeScatterTooltipPosition(
        event.clientX,
        event.clientY,
        scaleFactor,
      );

      // Use local tooltip state to avoid conflicts with other components
      setLocalTooltip({
        visible: true,
        content: tooltipContent,
        x: adjustedX,
        y: adjustedY
      });
      // Also set global tooltip for MapContainer in splitter view
      setTooltip({
        visible: true,
        content: tooltipContent,
        x: event.clientX,
        y: event.clientY
      });
    };

    const mousemove = (event, d) => {
      if (shouldDisableHover()) {return;}
      const tooltipContent = getTooltipContent(d);

      // Get the scale factor from CSS custom property
      const scaleFactor = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--scale-factor')) || 1;

      // Left-biased beside the cursor, flipped to the cursor's left near the right viewport edge so
      // the rightmost column of a chart grid doesn't hide its tooltip off screen.
      const { x: adjustedX, y: adjustedY } = computeScatterTooltipPosition(
        event.clientX,
        event.clientY,
        scaleFactor,
      );

      // Use local tooltip state to avoid conflicts with other components
      setLocalTooltip({
        visible: true,
        content: tooltipContent,
        x: adjustedX,
        y: adjustedY
      });
      // Also set global tooltip for MapContainer in splitter view
      setTooltip({
        visible: true,
        content: tooltipContent,
        x: event.clientX,
        y: event.clientY
      });
    };

    const mouseout = () => {
      setLocalTooltip({
        visible: false,
        content: "",
        x: 0,
        y: 0
      });
      // Also clear global tooltip for MapContainer in splitter view
      setTooltip({
        visible: false,
        content: "",
        x: 0,
        y: 0
      });
    };

    const click = (_event, d) => {
      if (isLassoActive || isComputingSelection) {return;}
      const drawnCoords = lastLassoInstance?.getDrawnCoords?.();
      if (drawnCoords && drawnCoords.length >= 2) {return;}

      // Set the selected time when a point is clicked
      if (d.Datetime) {
        try {
          const clickedTime = new Date(d.Datetime);
          if (!isNaN(clickedTime.getTime())) {
            // Request control of selectedTime
            if (requestTimeControl('scatter')) {
              setSelectedTime(clickedTime, 'scatter');
              debug('🔍 SimpleScatter: Selected time set to:', clickedTime.toISOString());

              // Release time control after setting the time
              setTimeout(() => {
                releaseTimeControl('scatter');
              }, 100);
            } else {
              debug('🔍 SimpleScatter: Time control denied - another component has higher priority');
            }
          }
        } catch (error: any) {
          logError('Error setting selected time:', error);
        }
      }
    };

    const getTooltipContent = (point) => {
      if (!point) {return "";}

      // Full date + time so points are unambiguous across days (history mode spans many dates).
      let timeString = "N/A";
      if (point.Datetime) {
        try {
          const date = point.Datetime instanceof Date ? point.Datetime : new Date(point.Datetime);
          if (!isNaN(date.getTime())) {
            const timezone = point.timezone || datasetTimezone() || undefined;
            timeString = formatDateTime(date, timezone) || "N/A";
          }
        } catch (error: any) {
          /* leave timeString as "N/A" on unparseable dates */
        }
      }

      const tooltipRows = [
        `<tr><td>DATETIME</td><td>${timeString}</td></tr>`,
        `<tr><td>${chart.xaxis.name.toUpperCase()}</td><td>${parseFloat(point.x || 0).toFixed(1)}</td></tr>`,
        `<tr><td>${chart.yaxis.name.toUpperCase()}</td><td>${parseFloat(point.y || 0).toFixed(1)}</td></tr>`
      ];

      // Add density information for clusters
      if (point.isCluster && point.density) {
        tooltipRows.push(`<tr><td>DENSITY</td><td>${point.density} pts</td></tr>`);
      }

      const pageColortype = (props as { colortype?: string }).colortype || "DEFAULT";
      if (
        pageColortype === "DEFAULT" &&
        chart.colorType === "By Channel" &&
        chart.colorChannel?.name
      ) {
        const channelName = chart.colorChannel.name;
        const channelValue =
          point[channelName] ??
          point[channelName.toLowerCase()] ??
          point[channelName.toUpperCase()];
        if (channelValue !== undefined && channelValue !== null && !isNaN(Number(channelValue))) {
          tooltipRows.push(
            `<tr><td>${channelName.toUpperCase()}</td><td>${parseFloat(channelValue).toFixed(1)}</td></tr>`
          );
        }
      }

      return `<table class='table-striped'>${tooltipRows.join('')}</table>`;
    };

    // Function to generate info tooltip content
    const getInfoTooltipContent = () => {
      const stats = dataProcessingStats();

      const tooltipRows = [];

      if (stats && stats.enhancedProcessing) {
        tooltipRows.push(`<tr><td colspan="2"><strong>DENSITY OPTIMIZATION</strong></td></tr>`);
        tooltipRows.push(`<tr><td>ORIGINAL POINTS</td><td>${stats.originalCount}</td></tr>`);
        tooltipRows.push(`<tr><td>OPTIMIZED POINTS</td><td>${stats.optimizedCount}</td></tr>`);
        tooltipRows.push(`<tr><td>GROUPS PROCESSED</td><td>${stats.groupsProcessed}</td></tr>`);
      }

      return `<table class='table-striped'>${tooltipRows.join('')}</table>`;
    };

    // Update the circles to reflect selection state with enhanced group rendering
    // Use filteredData so only selected points are visible; plotLayer clips overflow at top/bottom
    const circles = plotLayer
      .selectAll("circle")
      .data(displayData || []);

    // Remove exiting circles
    circles.exit().remove();

    // Track the actual rendered count for the label
    setActualRenderedCount(displayData ? displayData.length : 0);

    const circlesEnter = circles.enter()
      .append("circle")
      .attr("class", "xy-circle scatter")
      .attr("cx", (d) => {
        const x = xScale(d.x);
        return x;
      })
      .attr("cy", (d) => yScale(resolvePointY(d)))
      .style("r", (d) => {
        // Increase circle size based on density, max 4x current size (4px base for better visibility)
        const baseRadius = 4;
        const maxRadius = baseRadius * 4; // 16px max

        if (d.density && d.density > 1) {
          // Scale radius based on density, but cap at maxRadius
          const densityScale = Math.min(d.density / 10, 3); // Scale factor based on density
          return Math.min(baseRadius + densityScale, maxRadius);
        }

        return baseRadius; // Default size
      })
      .style("fill", (d) => getColor(chart, d))
      .style("opacity", (d) => getPointOpacity(d, pointOpacityCtx))
      .style("display", (d) => {
        if (!exploreLassoCut || !exploreCutViewActive) {return null;}
        return isPointVisibleInCut(d, currentCutPointIds, currentCutEvents) ? null : "none";
      })
      .style("stroke", "white")
      .style("stroke-width", (d) => getPointStrokeWidth(d, pointOpacityCtx.selectedIds))
      .style("stroke-opacity", 1)
      .style("pointer-events", "all") // Explicitly enable pointer events
      .style("cursor", "pointer") // Show pointer cursor
      .on("mouseover", mouseover)
      .on("mouseout", mouseout)
      .on("mousemove", mousemove)
      .on("click", click);

    addSelection(circlesEnter);

    // Update existing circles (merge enter selection with update selection)
    const allCircles = circles.merge(circlesEnter)
      .attr("cx", (d) => {
        const x = xScale(d.x);
        return x;
      })
      .attr("cy", (d) => yScale(resolvePointY(d)))
      .style("fill", (d) => getColor(chart, d))
      .style("opacity", (d) => getPointOpacity(d, pointOpacityCtx))
      .style("display", (d) => {
        if (!exploreLassoCut || !exploreCutViewActive) {return null;}
        return isPointVisibleInCut(d, currentCutPointIds, currentCutEvents) ? null : "none";
      })
      .style("stroke", "white")
      .style("stroke-width", (d) => getPointStrokeWidth(d, pointOpacityCtx.selectedIds))
      .style("stroke-opacity", 1);

    // Setup lasso (local selection only — does not write to selectionStore)
    try {
      if (svg && svg.node()) {
        const points = chartbody.selectAll(".scatter");
        const lassoInstance = lasso()
          .closePathSelect(true)
          // @ts-expect-error d3-lasso closePathDistance exists at runtime
          .closePathDistance(75)
          .items(points)
          .targetArea(svg)
          .skipDragCalculations(true)
          .on("start", () => {
            isLassoActive = true;
            isComputingSelection = false;
            setLocalTooltip({ visible: false, content: "", x: 0, y: 0 });
            setTooltip({ visible: false, content: "", x: 0, y: 0 });
          })
          .on("end", async () => {
            isLassoActive = false;
            const drawnCoords = lassoInstance.getDrawnCoords();
            if (!drawnCoords || drawnCoords.length < 3) {return;}

            isComputingSelection = true;
            setIsComputingLassoSelection(true);
            setSimpleScatterLassoComputingCursor(true);
            try {
              const scatterData = points.data() as Array<{ ID?: number }>;
              const scatterNodes = points.nodes() as Array<Element | null>;
              const pointData: Array<{ id: string; x: number; y: number }> = [];
              const len = Math.min(scatterData.length, scatterNodes.length);
              const lassoCutIds = props.cutPointIds?.() ?? new Set<number>();
              for (let i = 0; i < len; i++) {
                const d = scatterData[i];
                if (!d || d.ID === undefined || d.ID === null) {continue;}
                if (lassoCutIds.size > 0 && !lassoCutIds.has(Number(d.ID))) {continue;}
                const node = scatterNodes[i];
                if (!node) {continue;}
                const box = node.getBoundingClientRect();
                pointData.push({
                  id: String(d.ID),
                  x: box.left + box.width / 2,
                  y: box.top + box.height / 2,
                });
              }
              if (pointData.length === 0) {return;}

              if (!lassoWorker) {
                lassoWorker = new LassoSelectionWorker();
                if (!lassoWorker) {throw new Error("Failed to create lasso worker");}
              }
              const messageId = `simple-scatter-lasso-${Date.now()}-${Math.random()}`;
              const polygonLen = Array.isArray(drawnCoords) ? drawnCoords.length : 0;
              const timeoutMs = Math.min(
                120_000,
                Math.max(20_000, 12_000 + pointData.length * 8 + polygonLen * 40)
              );
              const selectedIdStrings = await new Promise<string[]>((resolve, reject) => {
                const timeout = setTimeout(() => {
                  if (lassoWorker) {lassoWorker.removeEventListener("message", handleMessage);}
                  reject(
                    new Error(
                      `Lasso selection computation timeout after ${timeoutMs}ms (${pointData.length} points, ${polygonLen} polygon vertices)`
                    )
                  );
                }, timeoutMs);
                const handleMessage = (event: MessageEvent) => {
                  if (event.data.id === messageId) {
                    clearTimeout(timeout);
                    if (lassoWorker) {lassoWorker.removeEventListener("message", handleMessage);}
                    if (event.data.type === "success" && event.data.result) {
                      resolve(event.data.result.selectedIds);
                    } else {
                      reject(new Error(event.data.error || "Unknown error"));
                    }
                  }
                };
                if (lassoWorker) {
                  lassoWorker.addEventListener("message", handleMessage);
                  lassoWorker.postMessage({
                    id: messageId,
                    type: "COMPUTE_LASSO_SELECTION",
                    data: { points: pointData, polygon: drawnCoords },
                    timestamp: Date.now(),
                  });
                } else {
                  clearTimeout(timeout);
                  reject(new Error("Worker not available"));
                }
              });

              if (selectedIdStrings.length > 0) {
                const numericIds = selectedIdStrings
                  .map((id) => Number(id))
                  .filter((id) => !isNaN(id));
                applyLassoSelection(numericIds);
              }
            } catch (error: unknown) {
              logError("Error computing lasso selection:", error);
            } finally {
              isComputingSelection = false;
              setIsComputingLassoSelection(false);
              setSimpleScatterLassoComputingCursor(false);
            }
          });
        lastLassoInstance = lassoInstance;
        svg.call(lassoInstance);
        allCircles.on("mousedown", function (event: MouseEvent) {
          event.stopPropagation();
        });
      }
    } catch (error: unknown) {
      logError("Error setting up lasso:", error);
    }

    // Add info icon to bottom right corner
    const infoIcon = chartbody.append("g")
      .attr("class", "info-icon")
      .attr("transform", `translate(${xywidth - 20}, ${xyheight - 20})`)
      .style("cursor", "pointer");

    addSelection(infoIcon);

    // Add circle background for info icon
    infoIcon.append("circle")
      .attr("r", 12)
      .style("fill", isDark() ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.9)")
      .style("stroke", isDark() ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)")
      .style("stroke-width", 1);

    // Add "i" text
    infoIcon.append("text")
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "middle")
      .attr("font-size", "14px")
      .attr("font-weight", "bold")
      .attr("font-family", "Arial, sans-serif")
      .style("fill", isDark() ? "black" : "white")
      .style("user-select", "none")
      .text("i");

    // Add mouse events for info icon
    infoIcon
      .on("mouseover", (event) => {
        const tooltipContent = getInfoTooltipContent();

        // Get the scale factor from CSS custom property
        const scaleFactor = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--scale-factor')) || 1;

        // Calculate adjusted position accounting for scaling
        const adjustedX = event.clientX - (100 / scaleFactor); // Move left 100px (scaled)
        const adjustedY = event.clientY - (200 / scaleFactor); // Move up 200px (scaled)

        setInfoTooltip({
          visible: true,
          content: tooltipContent,
          x: adjustedX,
          y: adjustedY
        });
      })
      .on("mouseout", () => {
        setInfoTooltip({
          visible: false,
          content: "",
          x: 0,
          y: 0
        });
      });

    if (renderGeneration !== chartRenderGeneration) {
      return;
    }

    setFitRenderContext({
      chart,
      regressionMethod: regressionMethod as RegressionMethod,
      colortype: currentColorType || "DEFAULT",
      fitSourceData: displayData,
      preCutFitSourceData: preCutFilteredData,
      yAxisChannelName,
      xywidth,
      xyheight,
    });

    // Notify parent once when initial chart render completes
    // Also mark scatter as rendered
    setTimeout(() => {
      setScatterRendered(true);
      signalReadyOnce();
    }, 0);
  });

  // No separate effect needed - regression is drawn in the main render effect above

  onCleanup(() => {
    if (updateTimer) {
      clearTimeout(updateTimer);
    }
    if (lassoWorker) {
      lassoWorker.terminate();
      lassoWorker = null;
    }
    isLassoActive = false;
    isComputingSelection = false;
    setIsComputingLassoSelection(false);
    setSimpleScatterLassoComputingCursor(false);
    destroyScatterUPlot();
    // D3 cleanup is now handled by useChartCleanup hook
    // DON'T destroy the singleton worker manager - it's shared across all scatter charts
    // and should persist for the lifetime of the application
    // enhancedScatterWorkerManager.destroy();
  });

  const isBusy = () => isLoading() || isProcessingData() || isCalculatingRegression();

  /**
   * In-plot legend overlay. A sibling of the chart container, never a child: the D3 render clears
   * the container's DOM wholesale on every redraw, so anything Solid owned inside it would be torn
   * out from under the framework.
   */
  const PlotLegends = () => (
    <Show when={colorChannelLegend()}>
      {(getModel) => (
        <div class="simple-scatter-plot-legends">
          <ScatterColorChannelLegend model={getModel()} />
        </div>
      )}
    </Show>
  );

  return (
    <div class="w-full" style={isBusy() ? { "min-height": "100vh" } : {}}>
      <Show when={!isLoading()}>
        {/* Filter Status Label */}
        <Show when={props.showFilterStatus !== false}>
          <div class="flex justify-center mb-3" style={{ "margin-top": "75px" }}>
            <Show
              when={props.chart?.filters && props.chart.filters.length > 0}
              fallback={
                <div class="px-3 py-1 text-xs font-medium rounded-full bg-grey-100 text-black-700 border border-grey-200">
                  unfiltered
                </div>
              }
            >
              <div class="flex gap-2 flex-wrap justify-center">
                <For each={props.chart.filters}>{(filter) => (
                  <span class="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 border border-green-200">
                    {filter}
                  </span>
                )}</For>
              </div>
            </Show>
          </div>
        </Show>


        {/* Chart container; when parent handles zoom, allow opting-in to show internal fit table */}
        <Show
          when={!props.handleZoom || props.showFitTable}
          fallback={
            <div style={{ position: "relative", width: "100%" }}>
              <PlotLegends />
              <div
                class="simple-scatter-chart-container"
                ref={(el) => (chartRef = el)}
                style={{ "padding-top": "0px", "min-height": "400px", "width": "100%" }}
              />
            </div>
          }
        >
          <div style={{
            width: "100%",
            height: isZoomed() ? "100%" : (props.totalCharts === 1 ? "75vh" : "auto"),
            "max-height": isZoomed() ? "100%" : (props.totalCharts === 1 ? "75vh" : "none"),
            display: "flex",
            "padding-top": props.totalCharts === 1 ? "0" : "0px",
            "min-height": props.totalCharts === 1 ? "75vh" : "400px",
            "box-sizing": "border-box",
            overflow: props.totalCharts === 1 ? "hidden" : "visible"
          }}>
            <div
              class="simple-scatter-chart-wrapper"
              style={{
                width: isZoomed() && fitData().length > 0 ? "66%" : "100%",
                height: isZoomed() ? "95%" : (props.totalCharts === 1 ? "100%" : "auto"),
                "max-height": isZoomed() ? "95%" : (props.totalCharts === 1 ? "100%" : "none"),
                "min-height": "400px",
                "min-width": "400px",
                "box-sizing": "border-box",
              }}
            >
              <Show when={isComputingLassoSelection()}>
                <div
                  class="simple-scatter-selection-status-banner"
                  role="status"
                  aria-live="polite"
                >
                  Computing selection...
                </div>
              </Show>
              <PlotLegends />
              <div
                class="simple-scatter-chart-container"
                ref={(el) => {
                  chartRef = el;
                  // Trigger a size update after ref is set to ensure ResizeObserver picks it up
                  if (el && resizeObserver) {
                    // Small delay to ensure DOM is laid out
                    requestAnimationFrame(() => {
                      const width = el.clientWidth || el.parentElement?.clientWidth || 0;
                      const height = el.clientHeight || el.parentElement?.clientHeight || 400;
                      if (width > 0 && height > 0) {
                        setContainerSize({ width, height });
                      }
                    });
                  }
                }}
                style={{
                  width: "100%",
                  height: "100%",
                  "box-sizing": "border-box",
                }}
               />
            </div>
            <Show when={props.chart?.missingChannels?.length > 0}>
              <div class="text-center text-sm text-gray-500 dark:text-gray-400 mt-1 w-full" style={{ clear: "both" }}>
                Data channels could not be loaded for this chart.
              </div>
            </Show>
            <Show when={isZoomed() && fitData().length > 0}>
              <div
                style={{
                  width: "33%",
                  height: "95%",
                  float: "left",
                  "margin-top": "100px",
                  "padding-left": "30px",
                }}
              >
                <Table
                  xaxis={(() => { const chart = (props.chart && props.chart.series && props.chart.series[0]) || {}; return chart.xaxis?.name || 'X'; })()}
                  fitData={fitData()}
                  xTicks={fitTableXTicks()}
                  version={fitDataVersion()}
                />
              </div>
            </Show>
            <div style={{ clear: "both" }} />
          </div>
        </Show>

        {/* Local tooltip for when not in splitter view */}
        <Show when={!document.querySelector('.mapboxgl-map') && localTooltip().visible}>
          <div
            id="scatter-tooltip"
            class="tooltip"
            style={{
              position: 'fixed',
              opacity: 1,
              left: `${localTooltip().x}px`,
              top: `${localTooltip().y}px`,
              "pointer-events": 'none',
              "z-index": 9999
            }}
            innerHTML={sanitizeTooltipHtml(localTooltip().content)}
          />
        </Show>

        {/* Info tooltip */}
        <Show when={infoTooltip().visible}>
          <div
            id="info-tooltip"
            class="tooltip"
            style={{
              position: 'fixed',
              opacity: 1,
              left: `${infoTooltip().x}px`,
              top: `${infoTooltip().y}px`,
              "pointer-events": 'none',
              "z-index": 9999
            }}
            innerHTML={sanitizeTooltipHtml(infoTooltip().content)}
          />
        </Show>
      </Show>
    </div>
  );
}